import React, { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';

const API_BASE_URL = process.env.NODE_ENV === 'production' ? '/api' : 'http://hiomenu.local/api';

interface BannerItem {
  id: number;
  name: string;
  image_url: string | null;
  position: 'superior' | 'lateral' | 'inferior';
  lateral_position: number | null;
  display_order?: number;
}

interface SmartBannerItem {
  id: number;
  name: string;
  banner_type?: 'extras' | 'bebidas_postres' | 'chips' | 'freebies' | 'combo' | 'postres';
  background: {
    type: 'color' | 'image';
    color: string | null;
    image_url: string | null;
  };
  header: {
    type: 'image' | 'text';
    image_url: string | null;
    text: string | null;
    font: {
      id: number;
      name: string;
      family: string;
      file_path: string | null;
      is_google_font: number;
      google_font_url: string | null;
    } | null;
    font_size: number;
    text_color: string;
    text_align: 'left' | 'center' | 'right';
  };
  items_config?: {
    text_font_size: number;
    price_font_size: number;
    text_color: string;
    price_color: string;
    text_font: {
      id: number;
      name: string;
      family: string;
      file_path: string | null;
      is_google_font: number;
      google_font_url: string | null;
    } | null;
    price_font: {
      id: number;
      name: string;
      family: string;
      file_path: string | null;
      is_google_font: number;
      google_font_url: string | null;
    } | null;
  };
  columns: number;
  items: Array<{
    id: number;
    item_type: 'product' | 'custom' | 'extra';
    name: string;
    price: number | null;
    image_url?: string | null;
    show_image: number;
    show_price: number;
    group_name?: string | null;
    display_order?: number;
  }>;
  display_order: number;
  position: 'superior' | 'lateral' | 'inferior';
  lateral_position: number | null;
}

interface ProductVariant {
  product_id: number;
  variant_label: string;
  price: number;
  image_url?: string | null;
  tiene_aguacate?: number;
  es_picante?: number;
  es_recomendado?: number;
}

interface ProductReference {
  reference_code: string;
  base_name: string;
  family?: string | null;
  description?: string | null;
  variants: ProductVariant[];
}

interface DisplayResponse {
  screen: {
    id: number;
    name: string;
    orientation: 'horizontal' | 'vertical';
    content_type: 'menu_only' | 'banners_only' | 'menu_and_banners' | 'advertising_only';
    slider_type?: 'fade' | 'slide' | 'zoom';
    banner_side: 'left' | 'right' | null;
    banner_divisions: number;
    products_count: number;
    colors: {
      background: string;
      title: string;
      subtitle: string;
      text: string;
      price_text?: string;
      price_number?: string;
    };
    fonts: {
      title?: { 
        family?: string | null; 
        size?: number | null;
        file_path?: string | null;
        is_google_font?: number;
        google_font_url?: string | null;
      };
      subtitle?: { 
        family?: string | null; 
        size?: number | null;
        file_path?: string | null;
        is_google_font?: number;
        google_font_url?: string | null;
      };
      text?: { 
        family?: string | null; 
        size?: number | null;
        file_path?: string | null;
        is_google_font?: number;
        google_font_url?: string | null;
      };
      script?: { 
        family?: string | null; 
        name?: string | null;
        file_path?: string | null;
        is_google_font?: number;
        google_font_url?: string | null;
      };
      price_text?: { 
        family?: string | null; 
        name?: string | null;
        size?: number | null;
        file_path?: string | null;
        is_google_font?: number;
        google_font_url?: string | null;
      };
      price_number?: { 
        family?: string | null; 
        name?: string | null;
        size?: number | null;
        file_path?: string | null;
        is_google_font?: number;
        google_font_url?: string | null;
      };
    };
  };
  establishment: {
    id: number;
    name: string;
    logo_url: string | null;
    language?: string | null;
  };
  banners: {
    superior: BannerItem[];
    laterales: BannerItem[];
    inferior?: BannerItem[];
  };
  smart_banners?: {
    superior: SmartBannerItem[];
    laterales: SmartBannerItem[];
    inferior?: SmartBannerItem[];
  };
  slider_images?: Array<{
    id: number;
    image_path: string;
    image_url?: string;
    display_order: number;
    duration: number;
    is_active: number;
  }>;
  combo_banners?: Array<{
    id: number;
    pantalla_id: number;
    producto_id: number;
    title_word1: string;
    title_word1_color: string;
    title_word2: string;
    title_word2_color: string;
    title_font_id: number | null;
    title_font_size: number;
    title_font?: {
      id: number;
      name: string;
      family: string;
      file_path: string | null;
      is_google_font: number;
      google_font_url: string | null;
    };
    combo_image_path: string;
    combo_image_url: string | null;
    product_name: string | null;
    product_name_background_color: string | null;
    product_name_font_id: number | null;
    product_name_font_size: number;
    product_name_font?: {
      id: number;
      name: string;
      family: string;
      file_path: string | null;
      is_google_font: number;
      google_font_url: string | null;
    };
    description: string | null;
    description_font_id: number | null;
    description_font_size: number;
    description_color: string;
    description_font?: {
      id: number;
      name: string;
      family: string;
      file_path: string | null;
      is_google_font: number;
      google_font_url: string | null;
    };
    legend_text: string;
    legend_font_id: number | null;
    legend_font_size: number;
    legend_text_color: string;
    legend_price_color: string;
    legend_font?: {
      id: number;
      name: string;
      family: string;
      file_path: string | null;
      is_google_font: number;
      google_font_url: string | null;
    };
    price_color: string;
    price_size: number;
    price_font_id: number | null;
    price_font?: {
      id: number;
      name: string;
      family: string;
      file_path: string | null;
      is_google_font: number;
      google_font_url: string | null;
    };
    background_color: string;
    duration: number;
    display_order: number;
    split_position: number | null;
    producto_nombre: string;
    producto_precio: number;
  }>;
  products: {
    references: ProductReference[];
    total: number;
  };
  icons?: {
    aguacate?: {
      id: number;
      tipo: string;
      name: string;
      image_path: string;
      image_url: string;
    };
    picante?: {
      id: number;
      tipo: string;
      name: string;
      image_path: string;
      image_url: string;
    };
    recomendado?: {
      id: number;
      tipo: string;
      name: string;
      image_path: string;
      image_url: string;
    };
  };
  extras?: {
    precio_hazlo_combo_regular?: string | null;
    precio_hazlo_combo_gigante?: string | null;
    precio_extras?: string | null;
    precio_extras_2?: string | null;
    precio_refresco_regular?: string | null;
    precio_refresco_large?: string | null;
    precio_refresco_bottle?: string | null;
    precio_chips?: string | null;
    precio_cookie?: string | null;
    precio_brownie?: string | null;
  };
}

export const DisplayScreen: React.FC = () => {
  const { token } = useParams<{ token: string }>();
  const [data, setData] = useState<DisplayResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!token) {
      setError('Token inválido');
      setLoading(false);
      return;
    }

    const controller = new AbortController();

    const loadData = async () => {
      setLoading(true);
      setError(null);
      try {
        // Agregar force_reload si está en la URL del frontend
      const urlParams = new URLSearchParams(window.location.search);
      const forceReload = urlParams.get('force_reload') === '1' ? '&force_reload=1' : '';
      
      // Agregar timestamp para evitar caché del navegador
      const timestamp = Date.now();
      const res = await fetch(`${API_BASE_URL}.php?route=display/screen&token=${encodeURIComponent(token)}${forceReload}&_t=${timestamp}`, {
          signal: controller.signal,
          cache: 'no-store' // Forzar que no use caché - esto es suficiente junto con el timestamp
        });
        const json = await res.json();

        if (!res.ok || json.error) {
          throw new Error(json.error || 'No se pudo cargar la pantalla');
        }

        const dataToSet = json.data || json;
        console.log('📦 [DisplayScreen] Datos recibidos del API:', {
          content_type: dataToSet.screen?.content_type,
          hasSmartBanners: !!dataToSet.smart_banners,
          smartBannersSuperior: dataToSet.smart_banners?.superior?.length || 0,
          smartBannersLaterales: dataToSet.smart_banners?.laterales?.length || 0,
          smartBannersLateralesData: dataToSet.smart_banners?.laterales,
          hasSuperiorBanners: dataToSet.banners?.superior?.length > 0,
          hasLateralBanners: dataToSet.banners?.laterales?.length > 0,
          icons: dataToSet.icons,
          iconsCount: dataToSet.icons ? Object.keys(dataToSet.icons).length : 0,
          iconTypes: dataToSet.icons ? Object.keys(dataToSet.icons) : [],
          timestamp: dataToSet.timestamp,
          data_fresh: dataToSet.data_fresh,
          fullResponse: dataToSet
        });
        
        // Log específico para items_config de smart banners
        if (dataToSet.smart_banners) {
          console.log('🎨 [DisplayScreen] Smart Banners - Items Config:');
          
          // Log para banners superiores
          if (dataToSet.smart_banners.superior && dataToSet.smart_banners.superior.length > 0) {
            dataToSet.smart_banners.superior.forEach((banner: any, index: number) => {
              console.log(`  📌 Banner Superior ${index + 1} (ID: ${banner.id}, Tipo: ${banner.banner_type}):`, {
                name: banner.name,
                banner_type: banner.banner_type,
                items_config: banner.items_config,
                text_font_size: banner.items_config?.text_font_size,
                price_font_size: banner.items_config?.price_font_size,
                text_color: banner.items_config?.text_color,
                price_color: banner.items_config?.price_color,
                has_items_config: !!banner.items_config
              });
            });
          }
          
          // Log para banners laterales
          if (dataToSet.smart_banners.laterales && dataToSet.smart_banners.laterales.length > 0) {
            dataToSet.smart_banners.laterales.forEach((banner: any, index: number) => {
              console.log(`  📌 Banner Lateral ${index + 1} (ID: ${banner.id}, Tipo: ${banner.banner_type}, Posición: ${banner.lateral_position}):`, {
                name: banner.name,
                banner_type: banner.banner_type,
                lateral_position: banner.lateral_position,
                items_config: banner.items_config,
                items_config_full: JSON.stringify(banner.items_config, null, 2), // Objeto completo expandido
                text_font_size: banner.items_config?.text_font_size,
                price_font_size: banner.items_config?.price_font_size,
                text_color: banner.items_config?.text_color,
                price_color: banner.items_config?.price_color,
                has_items_config: !!banner.items_config,
                // Mostrar todo el objeto del banner para ver qué campos llegan
                full_banner_object: banner
              });
            });
          }
        }
        
        setData(dataToSet);
      } catch (err: any) {
        if (err.name === 'AbortError') return;
        setError(err.message || 'Error al cargar la pantalla');
      } finally {
        setLoading(false);
      }
    };

    loadData();

    return () => controller.abort();
  }, [token]);

  // Función helper para limpiar el nombre de la fuente
  const cleanFontFamily = (fontFamily: string | null | undefined): string => {
    if (!fontFamily) return 'inherit';
    // Remover comillas simples, comillas dobles, espacios extra al inicio y final
    return fontFamily.replace(/^['"\s]+|['"\s]+$/g, '').trim();
  };

  // Función helper para determinar si el fondo es oscuro
  const isDarkBackground = (color: string | null | undefined): boolean => {
    if (!color) return false; // Por defecto, fondo claro
    
    // Normalizar el color (remover espacios, convertir a minúsculas)
    const normalizedColor = color.trim().toLowerCase();
    
    // Convertir hex a RGB
    const hex = normalizedColor.replace('#', '');
    if (hex.length !== 6) return false;
    
    // Validar que sean caracteres hexadecimales válidos
    if (!/^[0-9a-f]{6}$/.test(hex)) return false;
    
    const r = parseInt(hex.substr(0, 2), 16);
    const g = parseInt(hex.substr(2, 2), 16);
    const b = parseInt(hex.substr(4, 2), 16);
    
    // Calcular luminancia (brillo) usando la fórmula estándar
    const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
    
    // Si la luminancia es menor a 0.5, es un fondo oscuro
    return luminance < 0.5;
  };

  // Componente de estrella SVG que cambia de color según el fondo
  const StarIcon: React.FC<{ isDark: boolean; size?: string }> = ({ isDark, size = '1em' }) => (
    <svg 
      width={size} 
      height={size} 
      viewBox="0 0 24 24" 
      fill={isDark ? '#ffffff' : '#000000'}
      style={{ display: 'inline-block', verticalAlign: 'middle' }}
    >
      <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/>
    </svg>
  );

  // Función para cargar fuentes dinámicamente (reutilizable)
  const loadFont = (fontConfig: any, fontId: string) => {
    // LOGGING CRÍTICO - Ver qué recibe el frontend
    console.log(`[loadFont] ${fontId} - Config completa:`, JSON.stringify(fontConfig, null, 2));
    console.log(`[loadFont] ${fontId} - file_path RAW:`, fontConfig.file_path);
    console.log(`[loadFont] ${fontId} - file_path TYPE:`, typeof fontConfig.file_path);
    console.log(`[loadFont] ${fontId} - file_path LENGTH:`, fontConfig.file_path?.length);
    if (fontConfig.file_path) {
      const pathSubstring = fontConfig.file_path.substring(0, 100);
      console.log(`[loadFont] ${fontId} - file_path CHAR CODES (primeros 100):`, 
        [...pathSubstring].map(c => c.charCodeAt(0)).join(','));
    }
    
    if (!fontConfig?.family) {
      console.warn(`Fuente sin family configurada para ${fontId}`);
      return;
    }

    // Limpiar el nombre de la fuente
    const cleanFamily = cleanFontFamily(fontConfig.family);
    
    // Logging para depuración
    console.log(`[loadFont] ${fontId} - Family original: ${fontConfig.family}`);
    console.log(`[loadFont] ${fontId} - Family limpio: ${cleanFamily}`);
    console.log(`[loadFont] ${fontId} - Is Google Font: ${fontConfig.is_google_font}`);

    // Si ya está cargada, no hacer nada
    if (document.getElementById(fontId)) {
      console.log(`[loadFont] ${fontId} - Fuente ya cargada, omitiendo`);
      return;
    }

    // Verificar si es Google Font (puede venir como 1, 0, true, false)
    const isGoogleFont = fontConfig.is_google_font === 1 || fontConfig.is_google_font === true;

    // Si es Google Font
    if (isGoogleFont && fontConfig.google_font_url) {
      const link = document.createElement('link');
      link.id = fontId;
      link.rel = 'stylesheet';
      link.href = fontConfig.google_font_url;
      document.head.appendChild(link);
    } 
    // Si es fuente personalizada (tiene file_path y no es Google Font)
    else if (!isGoogleFont && fontConfig.file_path) {
      const style = document.createElement('style');
      style.id = fontId;
      // Determinar el formato del archivo
      // Limpiar el path: remover cualquier texto extra después de la extensión
      let filePath = fontConfig.file_path;
      if (filePath) {
        // Limpiar espacios y texto extra después de la extensión
        filePath = filePath.trim();
        filePath = filePath.replace(/\.(otf|ttf|woff|woff2|eot)(.*)$/i, '.$1');
        // Remover caracteres no válidos en URLs (mantener solo alfanuméricos, puntos, guiones, barras, :, /, ?, &, =, %)
        filePath = filePath.replace(/[^\/a-zA-Z0-9._:?&=%\-]/g, '');
        
        // Logging para depuración
        console.log(`[loadFont] Path original: ${fontConfig.file_path}`);
        console.log(`[loadFont] Path limpio: ${filePath}`);
      }
      
      // Extraer extensión del path original (no de la URL completa)
      let extension = 'woff2'; // default
      if (filePath) {
        // Si es una URL con parámetro path, extraer el path primero
        const urlMatch = filePath.match(/[?&]path=([^&]+)/);
        if (urlMatch) {
          const decodedPath = decodeURIComponent(urlMatch[1]);
          extension = decodedPath.split('.').pop()?.toLowerCase() || 'woff2';
        } else {
          // Si no es URL, usar directamente
          extension = filePath.split('.').pop()?.toLowerCase() || 'woff2';
        }
      }
      
      let format = 'woff2';
      if (extension === 'woff') format = 'woff';
      else if (extension === 'ttf') format = 'truetype';
      else if (extension === 'otf') format = 'opentype';
      else if (extension === 'eot') format = 'embedded-opentype';
      
      style.textContent = `
        @font-face {
          font-family: '${cleanFamily}';
          src: url('${filePath}') format('${format}');
          font-weight: normal;
          font-style: normal;
          font-display: swap;
        }
      `;
      document.head.appendChild(style);
      console.log(`[loadFont] ${fontId} - @font-face creado para '${cleanFamily}' con URL: ${filePath}`);
    } else {
      console.warn(`No se pudo cargar la fuente ${cleanFamily} para ${fontId}:`, {
        is_google_font: fontConfig.is_google_font,
        file_path: fontConfig.file_path,
        google_font_url: fontConfig.google_font_url
      });
    }
  };

  // Cargar fuentes dinámicamente
  useEffect(() => {
    if (!data?.screen?.fonts) return;

    // Cargar cada fuente
    if (data.screen.fonts.title) {
      loadFont(data.screen.fonts.title, 'font-title');
    }
    if (data.screen.fonts.subtitle) {
      loadFont(data.screen.fonts.subtitle, 'font-subtitle');
    }
    if (data.screen.fonts.text) {
      loadFont(data.screen.fonts.text, 'font-text');
    }

    // Cargar fuente Script para precios
    if (data.screen.fonts.script && data.screen.fonts.script.family && data.screen.fonts.script.file_path) {
      loadFont(data.screen.fonts.script, 'font-script');
    } else {
      console.warn('⚠️ Fuente Script no disponible o incompleta:', {
        script: data.screen.fonts.script,
        hasFamily: !!data.screen.fonts.script?.family,
        hasFilePath: !!data.screen.fonts.script?.file_path
      });
    }

    // Cargar fuente para texto de precios (Regular/Gigante)
    if (data.screen.fonts.price_text && data.screen.fonts.price_text.family && data.screen.fonts.price_text.file_path) {
      loadFont(data.screen.fonts.price_text, 'font-price-text');
    }

    // Cargar fuente para números de precios
    if (data.screen.fonts.price_number && data.screen.fonts.price_number.family && data.screen.fonts.price_number.file_path) {
      loadFont(data.screen.fonts.price_number, 'font-price-number');
    }
  }, [data]);

  // Cargar fuentes de combo banners dinámicamente
  useEffect(() => {
    if (!data?.combo_banners || !data.combo_banners.length) return;
    
    data.combo_banners.forEach((banner) => {
      // Cargar fuente del título
      if (banner.title_font?.family && banner.title_font?.file_path) {
        loadFont(banner.title_font, `combo-banner-${banner.id}-title-font`);
      }
      
      // Cargar fuente del nombre del producto
      if (banner.product_name_font?.family && banner.product_name_font?.file_path) {
        loadFont(banner.product_name_font, `combo-banner-${banner.id}-product-name-font`);
      }
      
      // Cargar fuente de la descripción
      if (banner.description_font?.family && banner.description_font?.file_path) {
        loadFont(banner.description_font, `combo-banner-${banner.id}-description-font`);
      }
      
      // Cargar fuente de la leyenda
      if (banner.legend_font?.family && banner.legend_font?.file_path) {
        loadFont(banner.legend_font, `combo-banner-${banner.id}-legend-font`);
      }
      
      // Cargar fuente del precio
      if (banner.price_font?.family && banner.price_font?.file_path) {
        loadFont(banner.price_font, `combo-banner-${banner.id}-price-font`);
      }
    });
  }, [data?.combo_banners]);

  const formatPrice = (value?: number | string | null) => {
    if (value === null || value === undefined) return '';
    const parsed = typeof value === 'number' ? value : parseFloat(value);
    if (Number.isNaN(parsed)) return '';
    return Math.round(parsed).toLocaleString('es-MX', { 
      style: 'currency', 
      currency: 'MXN',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    });
  };

  const themeStyles = useMemo(() => {
    if (!data) {
      return {};
    }

    return {
      backgroundColor: data.screen.colors.background || '#ffffff',
      color: data.screen.colors.text || '#000000',
      fontFamily: cleanFontFamily(data.screen.fonts?.text?.family) || 'GoodStuff-Text'
    };
  }, [data]);

  // Cargar fuentes de smart banners (header y items) si es necesario
  useEffect(() => {
    if (!data?.smart_banners) return;

    const loadSmartBannerFont = (fontConfig: any, fontId: string) => {
      if (!fontConfig?.family) return;

      if (document.getElementById(fontId)) return;

      const cleanFamily = cleanFontFamily(fontConfig.family);
      const isGoogleFont = fontConfig.is_google_font === 1 || fontConfig.is_google_font === true;

      if (isGoogleFont && fontConfig.google_font_url) {
        const link = document.createElement('link');
        link.id = fontId;
        link.rel = 'stylesheet';
        link.href = fontConfig.google_font_url;
        document.head.appendChild(link);
      } else if (!isGoogleFont && fontConfig.file_path) {
        const style = document.createElement('style');
        style.id = fontId;
        const extension = fontConfig.file_path.split('.').pop()?.toLowerCase() || 'woff2';
        let format = 'woff2';
        if (extension === 'woff') format = 'woff';
        else if (extension === 'ttf') format = 'truetype';
        else if (extension === 'otf') format = 'opentype';
        else if (extension === 'eot') format = 'embedded-opentype';
        
        style.textContent = `
          @font-face {
            font-family: '${cleanFamily}';
            src: url('${fontConfig.file_path}') format('${format}');
            font-weight: normal;
            font-style: normal;
            font-display: swap;
          }
        `;
        document.head.appendChild(style);
      }
    };

    // Cargar fuentes de todos los smart banners (header y items)
    [...(data.smart_banners.superior || []), ...(data.smart_banners.laterales || [])].forEach(banner => {
      if (banner.header?.font) {
        loadSmartBannerFont(banner.header.font, `smart-banner-header-font-${banner.id}`);
      }
      if (banner.items_config?.text_font) {
        loadSmartBannerFont(banner.items_config.text_font, `smart-banner-item-text-font-${banner.id}`);
      }
      if (banner.items_config?.price_font) {
        loadSmartBannerFont(banner.items_config.price_font, `smart-banner-item-price-font-${banner.id}`);
      }
    });
  }, [data?.smart_banners]);

  const renderSmartBanner = (banner: SmartBannerItem, isLateral: boolean = false, isVertical: boolean = false) => {
    if (!data) return null;

    const headerFontFamily = banner.header.font 
      ? cleanFontFamily(banner.header.font.family) 
      : 'inherit';

    // Altura del banner superior (misma que el banner superior de productos)
    const BANNER_SUPERIOR_HEIGHT = 200; // px (aumentado de 100 a 200)
    const HEADER_IMAGE_HEIGHT = 83; // Reducido a 1/3 del tamaño anterior menos 10px (280px / 3 ≈ 93px - 10px = 83px)
    
    // Determinar si es tipo 'extras', 'bebidas_postres', 'freebies' o 'combo' para aplicar estilos especiales
    const isExtrasType = banner.banner_type === 'extras';
    const isBebidasPostres = banner.banner_type === 'bebidas_postres';
    const isFreebiesType = banner.banner_type === 'freebies';
    const isComboType = banner.banner_type === 'combo';
    const isPostresType = banner.banner_type === 'postres';
    const isChipsType = banner.banner_type === 'chips';
    
    // Para lateral 3, usar imagen del header como background completo (excepto para tipo 'combo')
    const isLateral3 = isLateral && banner.lateral_position === 3;
    const useHeaderAsBackground = isLateral3 && banner.header.type === 'image' && banner.header.image_url && !isComboType;

    return (
      <div
        key={`smart-banner-${banner.id}`}
        className={isLateral || isVertical ? "w-full h-full overflow-hidden" : "w-full overflow-hidden"}
        style={{
          position: isLateral3 ? 'relative' : 'static', // Relative para posicionamiento absoluto del contenido en lateral 3
          backgroundColor: banner.background.type === 'color' 
            ? (banner.background.color || '#ffffff')
            : 'transparent',
          backgroundImage: useHeaderAsBackground
            ? `url(${banner.header.image_url})` // Usar imagen del header como background completo para lateral 3
            : (banner.background.type === 'image' && banner.background.image_url
              ? `url(${banner.background.image_url})`
              : 'none'),
          backgroundSize: 'cover', // Cubrir todo el espacio disponible
          backgroundPosition: 'center', // Centrar la imagen
          backgroundRepeat: 'no-repeat', // No repetir la imagen
          display: 'flex',
          flexDirection: 'column',
          ...(isLateral ? {
            height: '100%',
            maxHeight: '100%',
            minHeight: '100%',
            overflow: 'hidden'
          } : isVertical ? {
            // Para banners en vertical (inferiores), ocupar todo el espacio disponible
            height: '100%',
            maxHeight: '100%',
            minHeight: '100%',
            overflow: 'hidden'
          } : {
            maxHeight: banner.header.type === 'image' && banner.header.image_url 
              ? `${HEADER_IMAGE_HEIGHT}px` // 83px si hay imagen en el header (reducido a 1/3 menos 10px)
              : `${BANNER_SUPERIOR_HEIGHT}px`, // 200px si no hay imagen
            height: 'auto',
            overflow: 'hidden'
          })
        }}
      >
        {/* Header - No mostrar imagen del header si es lateral 3 (se usa como background completo) */}
        {banner.header.type === 'image' && banner.header.image_url && !useHeaderAsBackground && (
          <div 
            className="w-full flex-shrink-0" 
            style={{ 
              height: isVertical ? 'calc(100% / 3)' : undefined,
              maxHeight: isVertical ? 'calc(100% / 3)' : `${HEADER_IMAGE_HEIGHT}px`, 
              overflow: 'hidden'
            }}
          >
            <img
              src={banner.header.image_url}
              alt="Header"
              className="w-full h-full object-cover"
              style={{
                objectPosition: 'top',
                display: 'block',
                height: '100%',  // 100% del alto del contenedor del header
                maxHeight: '100%',
                width: '100%',
                objectFit: isVertical ? 'cover' : 'contain'  // cover para que cubra todo el espacio del header en vertical
              }}
            />
          </div>
        )}

        {banner.header.type === 'text' && banner.header.text && (
          <div
            className="w-full flex-shrink-0 px-4 py-2"
            style={{
              textAlign: banner.header.text_align || 'center'
            }}
          >
            <h3
              style={{
                fontFamily: headerFontFamily,
                fontSize: `${banner.header.font_size || 24}px`,
                color: (isExtrasType || isFreebiesType || isComboType || isPostresType) ? '#ffffff' : (banner.header.text_color || '#000000'),
                margin: 0,
                fontWeight: 'bold'
              }}
            >
              {banner.header.text}
            </h3>
          </div>
        )}

        {/* Items Grid */}
        {banner.items && banner.items.length > 0 && (() => {
          // Para tipo chips en vertical: mostrar solo un artículo personalizado con imagen y precio de tarifa
          if (isChipsType && isVertical) {
            // Buscar el primer item personalizado (custom) o el primer item disponible
            const chipsItem = banner.items.find(item => item.item_type === 'custom') || banner.items[0];
            const precioChips = data?.extras?.precio_chips;
            
            if (!chipsItem) return null;
            
            return (
              <div className="flex items-center justify-center" style={{ gap: '12px', padding: isLateral ? '2px 48px' : '4px 48px' }}>
                {/* Columna izquierda: Nombre y Precio - Ancho reducido */}
                <div className="flex flex-col items-center justify-center" style={{ gap: '4px', width: '30%', flexShrink: 0 }}>
                  {/* Nombre del artículo con fondo negro */}
                  {chipsItem.name && (
                    <div style={{ textAlign: 'center', width: '100%' }}>
                      <p
                        style={{
                          color: '#ffffff',
                          backgroundColor: '#000000',
                          padding: '4px 8px',
                          borderRadius: '0px',
                          fontSize: `${banner.items_config?.text_font_size || (data.screen.fonts?.text?.size || 18)}px`,
                          fontFamily: banner.items_config?.text_font 
                            ? `'${cleanFontFamily(banner.items_config.text_font.family)}'` 
                            : (data.screen.fonts?.text?.family ? `'${cleanFontFamily(data.screen.fonts.text.family)}'` : 'inherit'),
                          margin: 0,
                          fontWeight: 'bold',
                          lineHeight: '1.0',
                          display: 'inline-block',
                          textAlign: 'center'
                        }}
                      >
                        {chipsItem.name.toUpperCase()}
                      </p>
                    </div>
                  )}
                  
                  {/* Precio de tarifa */}
                  {precioChips && (
                    <div style={{ textAlign: 'center', width: '100%' }}>
                      <p
                        style={{
                          color: banner.items_config?.price_color || data.screen.colors.price_number || data.screen.colors.text || '#000000',
                          fontSize: `${banner.items_config?.price_font_size || (data.screen.fonts?.price_number?.size || data.screen.fonts?.text?.size || 20)}px`,
                          fontFamily: banner.items_config?.price_font 
                            ? `'${cleanFontFamily(banner.items_config.price_font.family)}'` 
                            : (data.screen.fonts?.price_number?.family 
                                ? `'${cleanFontFamily(data.screen.fonts.price_number.family)}'` 
                                : (data.screen.fonts?.text?.family ? `'${cleanFontFamily(data.screen.fonts.text.family)}'` : 'inherit')),
                          margin: 0,
                          fontWeight: 'bold',
                          lineHeight: '1.0',
                          textAlign: 'center'
                        }}
                      >
                        ${Math.round(parseFloat(precioChips))}
                      </p>
                    </div>
                  )}
                </div>
                
                {/* Columna derecha: Imagen - Más grande */}
                {chipsItem.show_image === 1 && chipsItem.image_url && (
                  <div className="flex items-center justify-center" style={{ width: '70%', flexShrink: 0, height: '100%' }}>
                    <img
                      src={chipsItem.image_url}
                      alt={chipsItem.name}
                      className="object-contain"
                      style={{
                        maxHeight: '200%',
                        maxWidth: '200%',
                        height: 'auto',
                        width: 'auto',
                        objectFit: 'contain'
                      }}
                    />
                  </div>
                )}
              </div>
            );
          }
          
          // Para lateral 3: layout especial (título y precio juntos, superior izquierda, texto más grande)
          if (isLateral3 && banner.items.length > 0) {
            const item = banner.items[0]; // Solo el primer item
            
            return (
              <div
                style={{
                  position: 'absolute',
                  top: '20px',
                  left: '20px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px',
                  zIndex: 10
                }}
              >
                {/* Título y precio juntos */}
                <p
                  className="font-semibold"
                  style={{
                    color: '#000000', // Texto negro para lateral 3
                    fontSize: `${banner.items_config?.text_font_size || (data.screen.fonts?.text?.size || 18)}px`,
                    fontFamily: banner.items_config?.text_font 
                      ? `'${cleanFontFamily(banner.items_config.text_font.family)}'` 
                      : (data.screen.fonts?.text?.family ? `'${cleanFontFamily(data.screen.fonts.text.family)}'` : 'inherit'),
                    margin: 0,
                    lineHeight: '1.2',
                    textTransform: 'uppercase'
                  }}
                >
                  {item.name}
                </p>
                
                {/* Precio con fondo rojo */}
                {item.show_price === 1 && item.price !== null && item.price !== undefined && (
                  <div
                    style={{
                      backgroundColor: '#ff0000',
                      padding: '6px 12px',
                      borderRadius: '4px',
                      flexShrink: 0
                    }}
                  >
                    <p
                      className="font-bold"
                      style={{
                        color: '#ffffff',
                        fontSize: `${banner.items_config?.price_font_size || (data.screen.fonts?.price_number?.size || data.screen.fonts?.text?.size || 20)}px`,
                        fontFamily: banner.items_config?.price_font 
                          ? `'${cleanFontFamily(banner.items_config.price_font.family)}'` 
                          : (data.screen.fonts?.price_number?.family 
                              ? `'${cleanFontFamily(data.screen.fonts.price_number.family)}'` 
                              : (data.screen.fonts?.text?.family ? `'${cleanFontFamily(data.screen.fonts.text.family)}'` : 'inherit')),
                        margin: 0,
                        lineHeight: '1.2',
                        whiteSpace: 'nowrap'
                      }}
                    >
                      ${Math.round(typeof item.price === 'number' ? item.price : parseFloat(String(item.price || '0')))}
                    </p>
                  </div>
                )}
              </div>
            );
          }
          
          // Para tipo bebidas_postres, separar items por familia
          if (isBebidasPostres) {
            // Filtrar items según orientación
            let bebidasItems, dessertsItems;
            
            if (isVertical) {
              // En vertical: solo bebidas (excluir galletas/postres)
              bebidasItems = banner.items.filter(item => 
                item.group_name !== 'Desserts' && 
                !item.name.toLowerCase().includes('galleta') && 
                !item.name.toLowerCase().includes('cookie')
              );
              dessertsItems = []; // No mostrar desserts en vertical
              
              // Obtener precios de la tarifa
              const precioRegular = data?.extras?.precio_refresco_regular;
              const precioGrande = data?.extras?.precio_refresco_large;
              const precioAgua = data?.extras?.precio_refresco_bottle;
              
              // Mapear items a formatos por display_order
              // display_order 1 = Regular, 2 = Grande, 3 = Agua
              const itemRegular = bebidasItems.find(item => (item.display_order ?? 0) === 1);
              const itemGrande = bebidasItems.find(item => (item.display_order ?? 0) === 2);
              const itemAgua = bebidasItems.find(item => (item.display_order ?? 0) === 3);
              
              // Obtener imágenes de bebidas (logos de marcas) - TODAS las imágenes disponibles
              // Igual que en horizontal: items con show_image === 1 e image_url
              const bebidasImages = bebidasItems
                .filter(item => {
                  return item.show_image === 1 && item.image_url;
                })
                .map(item => ({
                  url: item.image_url!,
                  name: item.name,
                  displayOrder: item.display_order ?? 0
                }));
              
              // Layout vertical: 2 columnas (ancha con precios, estrecha con imágenes)
              return (
                <div className="flex h-full w-full flex-col" style={{ gap: '8px', padding: isLateral ? '2px 48px' : '4px 48px' }}>
                  <div className="flex" style={{ gap: '8px', flex: 1 }}>
                    {/* Columna izquierda (más ancha): Precios y formatos */}
                    <div className="flex flex-col flex-1" style={{ width: '70%', gap: '8px', justifyContent: 'center' }}>
                      {/* REGULAR - display_order 1 */}
                      {itemRegular && precioRegular && (
                        <div style={{ marginBottom: '12px' }}>
                          <div style={{ 
                            backgroundColor: '#000000', 
                            padding: '4px 8px',
                            borderRadius: '0px'
                          }}>
                            <p style={{
                              color: '#ffffff',
                              fontSize: `${banner.items_config?.text_font_size || (data.screen.fonts?.text?.size || 18)}px`,
                              fontFamily: banner.items_config?.text_font 
                                ? `'${cleanFontFamily(banner.items_config.text_font.family)}'` 
                                : (data.screen.fonts?.text?.family ? `'${cleanFontFamily(data.screen.fonts.text.family)}'` : 'inherit'),
                              margin: 0,
                              fontWeight: 'bold',
                              lineHeight: '1.0'
                            }}>
                              {itemRegular.name.toUpperCase()}
                            </p>
                          </div>
                          <p style={{
                            color: '#ffffff',
                            fontSize: `${banner.items_config?.price_font_size || (data.screen.fonts?.price_number?.size || data.screen.fonts?.text?.size || 20)}px`,
                            fontFamily: banner.items_config?.price_font 
                              ? `'${cleanFontFamily(banner.items_config.price_font.family)}'` 
                              : (data.screen.fonts?.price_number?.family 
                                  ? `'${cleanFontFamily(data.screen.fonts.price_number.family)}'` 
                                  : (data.screen.fonts?.text?.family ? `'${cleanFontFamily(data.screen.fonts.text.family)}'` : 'inherit')),
                            margin: '4px 0 0 0',
                            fontWeight: 'bold',
                            lineHeight: '1.0'
                          }}>
                            ${Math.round(parseFloat(precioRegular))}
                          </p>
                        </div>
                      )}
                      
                      {/* GRANDE - display_order 2 */}
                      {itemGrande && precioGrande && (
                        <div style={{ marginBottom: '12px' }}>
                          <div style={{ 
                            backgroundColor: '#000000', 
                            padding: '4px 8px',
                            borderRadius: '0px'
                          }}>
                            <p style={{
                              color: '#ffffff',
                              fontSize: `${banner.items_config?.text_font_size || (data.screen.fonts?.text?.size || 18)}px`,
                              fontFamily: banner.items_config?.text_font 
                                ? `'${cleanFontFamily(banner.items_config.text_font.family)}'` 
                                : (data.screen.fonts?.text?.family ? `'${cleanFontFamily(data.screen.fonts.text.family)}'` : 'inherit'),
                              margin: 0,
                              fontWeight: 'bold',
                              lineHeight: '1.0'
                            }}>
                              {itemGrande.name.toUpperCase()}
                            </p>
                          </div>
                          <p style={{
                            color: '#ffffff',
                            fontSize: `${banner.items_config?.price_font_size || (data.screen.fonts?.price_number?.size || data.screen.fonts?.text?.size || 20)}px`,
                            fontFamily: banner.items_config?.price_font 
                              ? `'${cleanFontFamily(banner.items_config.price_font.family)}'` 
                              : (data.screen.fonts?.price_number?.family 
                                  ? `'${cleanFontFamily(data.screen.fonts.price_number.family)}'` 
                                  : (data.screen.fonts?.text?.family ? `'${cleanFontFamily(data.screen.fonts.text.family)}'` : 'inherit')),
                            margin: '4px 0 0 0',
                            fontWeight: 'bold',
                            lineHeight: '1.0'
                          }}>
                            ${Math.round(parseFloat(precioGrande))}
                          </p>
                        </div>
                      )}
                      
                      {/* AGUA - display_order 3 */}
                      {itemAgua && precioAgua && (
                        <div>
                          <div style={{ 
                            backgroundColor: '#000000', 
                            padding: '4px 8px',
                            borderRadius: '0px'
                          }}>
                            <p style={{
                              color: '#ffffff',
                              fontSize: `${banner.items_config?.text_font_size || (data.screen.fonts?.text?.size || 18)}px`,
                              fontFamily: banner.items_config?.text_font 
                                ? `'${cleanFontFamily(banner.items_config.text_font.family)}'` 
                                : (data.screen.fonts?.text?.family ? `'${cleanFontFamily(data.screen.fonts.text.family)}'` : 'inherit'),
                              margin: 0,
                              fontWeight: 'bold',
                              lineHeight: '1.0'
                            }}>
                              {itemAgua.name.toUpperCase()}
                            </p>
                          </div>
                          <p style={{
                            color: '#ffffff',
                            fontSize: `${banner.items_config?.price_font_size || (data.screen.fonts?.price_number?.size || data.screen.fonts?.text?.size || 20)}px`,
                            fontFamily: banner.items_config?.price_font 
                              ? `'${cleanFontFamily(banner.items_config.price_font.family)}'` 
                              : (data.screen.fonts?.price_number?.family 
                                  ? `'${cleanFontFamily(data.screen.fonts.price_number.family)}'` 
                                  : (data.screen.fonts?.text?.family ? `'${cleanFontFamily(data.screen.fonts.text.family)}'` : 'inherit')),
                            margin: '4px 0 0 0',
                            fontWeight: 'bold',
                            lineHeight: '1.0'
                          }}>
                            ${Math.round(parseFloat(precioAgua))}
                          </p>
                        </div>
                      )}
                    </div>
                    
                    {/* Columna derecha (más estrecha): Imágenes/logos de bebidas */}
                    <div className="flex flex-col" style={{ width: '30%', gap: '8px', justifyContent: 'center', alignItems: 'center' }}>
                      {bebidasImages.length > 0 ? (
                        bebidasImages.map((imageItem, index) => (
                          <img
                            key={`bebida-img-${imageItem.displayOrder}-${index}`}
                            src={imageItem.url}
                            alt={imageItem.name}
                            className="object-contain"
                            style={{
                              maxHeight: '100%',  // 100% de altura en vertical
                              maxWidth: '100%',
                              height: 'auto',
                              width: 'auto'
                            }}
                          />
                        ))
                      ) : null}
                    </div>
                  </div>
                </div>
              );
            }
            
            // En horizontal: mantener comportamiento original
            bebidasItems = banner.items.filter(item => item.group_name !== 'Desserts');
            dessertsItems = banner.items.filter(item => item.group_name === 'Desserts');
            
            // Función auxiliar para renderizar un item de bebida (layout horizontal)
            const renderBebidaItem = (item: typeof banner.items[0]) => (
              <div
                key={item.id}
                className="flex items-center justify-center gap-2"
                style={{
                  padding: isLateral ? '0px 4px' : '4px 4px'
                }}
              >
                <div className="flex items-center gap-2 flex-1 min-w-0">
                  {item.show_image === 1 && item.image_url && (
                    <img
                      src={item.image_url}
                      alt={item.name}
                      className="w-8 h-8 object-cover rounded flex-shrink-0"
                    />
                  )}
                  <p
                    className="font-semibold truncate"
                    style={{
                      color: '#ffffff', // Texto blanco para bebidas_postres
                      fontSize: `${banner.items_config?.text_font_size || (data.screen.fonts?.text?.size || 18)}px`,
                      fontFamily: banner.items_config?.text_font 
                        ? `'${cleanFontFamily(banner.items_config.text_font.family)}'` 
                        : (data.screen.fonts?.text?.family ? `'${cleanFontFamily(data.screen.fonts.text.family)}'` : 'inherit'),
                      margin: 0,
                      lineHeight: '1.2',
                      textTransform: 'uppercase'
                    }}
                  >
                    {item.name}
                  </p>
                </div>
                {item.show_price === 1 && item.price !== null && item.price !== undefined && (
                  <div
                    style={{
                      backgroundColor: '#ff0000', // Fondo rojo para el precio
                      padding: '4px 8px',
                      borderRadius: '4px',
                      flexShrink: 0
                    }}
                  >
                    <p
                      className="font-bold"
                      style={{
                        color: '#ffffff', // Texto blanco sobre fondo rojo
                        fontSize: `${banner.items_config?.price_font_size || (data.screen.fonts?.price_number?.size || data.screen.fonts?.text?.size || 20)}px`,
                        fontFamily: banner.items_config?.price_font 
                          ? `'${cleanFontFamily(banner.items_config.price_font.family)}'` 
                          : (data.screen.fonts?.price_number?.family 
                              ? `'${cleanFontFamily(data.screen.fonts.price_number.family)}'` 
                              : (data.screen.fonts?.text?.family ? `'${cleanFontFamily(data.screen.fonts.text.family)}'` : 'inherit')),
                        margin: 0,
                        lineHeight: '1.2',
                        whiteSpace: 'nowrap'
                      }}
                    >
                      ${Math.round(typeof item.price === 'number' ? item.price : parseFloat(String(item.price || '0')))}
                    </p>
                  </div>
                )}
              </div>
            );

            // Función auxiliar para renderizar un item de Dessert (layout vertical: título, precio, imagen abajo)
            const renderDessertItem = (item: typeof banner.items[0]) => (
              <div
                key={item.id}
                className="flex flex-col items-center justify-center"
                style={{
                  padding: isLateral ? '8px 4px' : '8px 4px',
                  gap: '8px'
                }}
              >
                {/* Título */}
                <p
                  className="font-semibold text-center"
                  style={{
                    color: '#ffffff',
                    fontSize: `${banner.items_config?.text_font_size || (data.screen.fonts?.text?.size || 18)}px`,
                    fontFamily: banner.items_config?.text_font 
                      ? `'${cleanFontFamily(banner.items_config.text_font.family)}'` 
                      : (data.screen.fonts?.text?.family ? `'${cleanFontFamily(data.screen.fonts.text.family)}'` : 'inherit'),
                    margin: 0,
                    lineHeight: '1.2',
                    textTransform: 'uppercase'
                  }}
                >
                  {item.name}
                </p>
                
                {/* Precio */}
                {item.show_price === 1 && item.price !== null && item.price !== undefined && (
                  <div
                    style={{
                      backgroundColor: '#ff0000',
                      padding: '4px 8px',
                      borderRadius: '4px'
                    }}
                  >
                    <p
                      className="font-bold"
                      style={{
                        color: '#ffffff',
                        fontSize: `${banner.items_config?.price_font_size || (data.screen.fonts?.price_number?.size || data.screen.fonts?.text?.size || 20)}px`,
                        fontFamily: banner.items_config?.price_font 
                          ? `'${cleanFontFamily(banner.items_config.price_font.family)}'` 
                          : (data.screen.fonts?.price_number?.family 
                              ? `'${cleanFontFamily(data.screen.fonts.price_number.family)}'` 
                              : (data.screen.fonts?.text?.family ? `'${cleanFontFamily(data.screen.fonts.text.family)}'` : 'inherit')),
                        margin: 0,
                        lineHeight: '1.2',
                        whiteSpace: 'nowrap'
                      }}
                    >
                      ${Math.round(typeof item.price === 'number' ? item.price : parseFloat(String(item.price || '0')))}
                    </p>
                  </div>
                )}
                
                {/* Imagen centrada abajo */}
                {item.show_image === 1 && item.image_url && (
                  <div className="w-full flex justify-center mt-2">
                    <img
                      src={item.image_url}
                      alt={item.name}
                      className="object-contain"
                      style={{
                        maxHeight: isLateral ? '120px' : '100px', // Imagen más pequeña
                        height: 'auto',
                        width: 'auto',
                        maxWidth: '100%'
                      }}
                    />
                  </div>
                )}
              </div>
            );
            
            const baseFontSize = banner.items_config?.text_font_size || (data.screen.fonts?.text?.size || 18);
            const fontSize = baseFontSize; // Usar el valor configurado directamente
            const rowHeight = isLateral ? `${fontSize + 20}px` : `${fontSize + 22}px`;
            
            return (
              <div
                className="flex-1 overflow-hidden"
                style={{
                  display: 'grid',
                  gridTemplateColumns: '1fr 1fr', // Dos columnas fijas
                  gap: '0px',
                  padding: isLateral ? '4px 20px' : '8px 12px',
                  gridAutoRows: rowHeight,
                  alignItems: 'center',
                  alignContent: 'center'
                }}
              >
                {/* Columna izquierda: Bebidas (todos excepto Desserts) */}
                <div 
                  style={{ 
                    borderRight: '2px solid #ffffff',
                    paddingRight: '8px',
                    marginRight: '8px',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '0px'
                  }}
                >
                  {bebidasItems.map(renderBebidaItem)}
                </div>
                
                {/* Columna derecha: Desserts */}
                <div 
                  style={{ 
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '0px'
                  }}
                >
                  {dessertsItems.map(renderDessertItem)}
                </div>
              </div>
            );
          }
          
          // Para tipo 'extras': layout especial con 70/30 y leyendas
          if (isExtrasType) {
            // Obtener precios de la tarifa
            const regularPrice = data?.extras?.precio_extras; // Columna 60%
            const gigantePrice = data?.extras?.precio_extras_2; // Columna 40%
            
            // Dividir items en grupos:
            const leftColumnItems = banner.items.slice(0, 14); // Primeros 14 items
            const rightColumnItems = banner.items.slice(14, 21); // Items 15-21 (hasta 7)
            
            // Dividir columna izquierda en 2 subcolumnas de 7 items cada una
            const leftSubCol1 = leftColumnItems.slice(0, 7); // Items 0-6
            const leftSubCol2 = leftColumnItems.slice(7, 14); // Items 7-13
            
            // Función auxiliar para renderizar un item
            const renderExtraItem = (item: typeof banner.items[0]) => (
              <div
                key={item.id}
                className="flex items-center gap-2"
                style={{
                  padding: '2px 4px'
                }}
              >
                {/* Columna 1: Artículo */}
                <div className="flex items-center gap-2 flex-1 min-w-0">
                  {item.show_image === 1 && item.image_url && (
                    <img
                      src={item.image_url}
                      alt={item.name}
                      className="w-12 h-16 object-cover rounded flex-shrink-0"
                    />
                  )}
                  <p
                    className="font-normal truncate"
                    style={{
                      color: '#ffffff',
                      fontSize: `${banner.items_config?.text_font_size || (data.screen.fonts?.text?.size || 18)}px`,
                      fontFamily: banner.items_config?.text_font 
                        ? `'${cleanFontFamily(banner.items_config.text_font.family)}'` 
                        : (data.screen.fonts?.text?.family ? `'${cleanFontFamily(data.screen.fonts.text.family)}'` : 'inherit'),
                      margin: 0,
                      lineHeight: '0.85', // Reducido de 1.0 a 0.85
                      textTransform: 'none'
                    }}
                  >
                    {item.name}
                  </p>
                </div>
                
                {/* Columna 2: Precio con fondo negro */}
                {item.show_price === 1 && item.price !== null && item.price !== undefined && (
                  <div
                    style={{
                      backgroundColor: '#000000',
                      padding: isVertical ? '2px 30px' : '2px 6px',  // 30px solo en vertical, 6px en horizontal
                      borderRadius: '0px', // Sin border-radius
                      flexShrink: 0,
                      width: isVertical ? 'auto' : undefined,  // Solo el ancho necesario en vertical
                      maxWidth: isVertical ? 'fit-content' : undefined  // No ocupar todo el ancho en vertical
                    }}
                  >
                    <p
                      className="font-bold"
                      style={{
                        color: '#ffffff',
                        fontSize: `${banner.items_config?.price_font_size || (data.screen.fonts?.price_number?.size || data.screen.fonts?.text?.size || 20)}px`,
                        fontFamily: banner.items_config?.price_font 
                          ? `'${cleanFontFamily(banner.items_config.price_font.family)}'` 
                          : (data.screen.fonts?.price_number?.family 
                              ? `'${cleanFontFamily(data.screen.fonts.price_number.family)}'` 
                              : (data.screen.fonts?.text?.family ? `'${cleanFontFamily(data.screen.fonts.text.family)}'` : 'inherit')),
                        margin: 0,
                        lineHeight: '1.2',
                        whiteSpace: 'nowrap'
                      }}
                    >
                      +${Math.round(typeof item.price === 'number' ? item.price : parseFloat(String(item.price || '0')))}
                    </p>
                  </div>
                )}
              </div>
            );
            
            // Ajustar padding según orientación
            const containerPadding = isVertical 
              ? '2px 4px'  // Padding reducido para vertical
              : (isLateral ? '2px 48px' : '4px 48px');  // Padding original para horizontal
            
            return (
              <div className="flex h-full w-full" style={{ gap: '4px', padding: containerPadding }}>
                {/* Contenedor del contenido - sin padding aquí */}
                <div className="flex h-full w-full">
                  {/* Columna Izquierda: 60% con padding izquierdo solo en vertical */}
                  <div className="flex flex-col" style={{ width: '60%', gap: '4px', paddingLeft: isVertical ? '20px' : undefined }}>
                  {/* Leyenda con precio_extras */}
                  {regularPrice && (
                    <div 
                      style={{
                        backgroundColor: '#000000',
                        padding: '4px 8px',
                        borderRadius: '0px', // Sin border-radius
                        textAlign: 'center'
                      }}
                    >
                      <p
                        style={{
                          color: '#ffffff',
                          fontSize: `${banner.items_config?.price_font_size || (data.screen.fonts?.price_number?.size || data.screen.fonts?.text?.size || 20)}px`,
                          fontFamily: banner.items_config?.price_font 
                            ? `'${cleanFontFamily(banner.items_config.price_font.family)}'` 
                            : (data.screen.fonts?.price_number?.family 
                                ? `'${cleanFontFamily(data.screen.fonts.price_number.family)}'` 
                                : (data.screen.fonts?.text?.family ? `'${cleanFontFamily(data.screen.fonts.text.family)}'` : 'inherit')),
                          margin: 0,
                          fontWeight: 'bold',
                          lineHeight: '1.2'
                        }}
                      >
                        +${Math.round(parseFloat(regularPrice))}
                      </p>
                    </div>
                  )}
                  
                  {/* Subcolumnas de productos */}
                  <div className="flex" style={{ gap: '4px', flex: 1, overflow: 'auto' }}>
                    {/* Subcolumna 1: 7 productos */}
                    <div className="flex flex-col flex-1" style={{ gap: '2px' }}>
                      {leftSubCol1.map(renderExtraItem)}
                    </div>
                    
                    {/* Subcolumna 2: 7 productos */}
                    <div className="flex flex-col flex-1" style={{ gap: '2px' }}>
                      {leftSubCol2.map(renderExtraItem)}
                    </div>
                  </div>
                </div>
                
                {/* Separador vertical centrado */}
                <div style={{ width: '1px', backgroundColor: '#ffffff', margin: '0 2px', alignSelf: 'stretch' }} />
                
                {/* Columna Derecha: 40% con padding derecho solo en vertical */}
                <div className="flex flex-col flex-1" style={{ width: '40%', gap: '4px', paddingRight: isVertical ? '20px' : undefined }}>
                  {/* Leyenda con precio_extras_2 */}
                  {gigantePrice && (
                    <div 
                      style={{
                        backgroundColor: '#000000',
                        padding: '4px 8px',
                        borderRadius: '0px', // Sin border-radius
                        textAlign: 'center'
                      }}
                    >
                      <p
                        style={{
                          color: '#ffffff',
                          fontSize: `${banner.items_config?.price_font_size || (data.screen.fonts?.price_number?.size || data.screen.fonts?.text?.size || 20)}px`,
                          fontFamily: banner.items_config?.price_font 
                            ? `'${cleanFontFamily(banner.items_config.price_font.family)}'` 
                            : (data.screen.fonts?.price_number?.family 
                                ? `'${cleanFontFamily(data.screen.fonts.price_number.family)}'` 
                                : (data.screen.fonts?.text?.family ? `'${cleanFontFamily(data.screen.fonts.text.family)}'` : 'inherit')),
                          margin: 0,
                          fontWeight: 'bold',
                          lineHeight: '1.2'
                        }}
                      >
                        +${Math.round(parseFloat(gigantePrice))}
                      </p>
                    </div>
                  )}
                  
                  {/* Productos de la columna derecha */}
                  <div className="flex flex-col" style={{ gap: '2px', flex: 1, overflow: 'auto' }}>
                    {rightColumnItems.map(renderExtraItem)}
                  </div>
                </div>
                </div>
              </div>
            );
          }
          
          // Para tipo 'freebies': layout especial con 3 columnas iguales
          if (isFreebiesType) {
            // Dividir items en grupos según orientación
            let column1Items, column2Items;
            
            if (isVertical) {
              // En vertical: 2 columnas, distribuir items equitativamente
              const totalItems = banner.items.length;
              const midPoint = Math.ceil(totalItems / 2);
              column1Items = banner.items.slice(0, midPoint);
              column2Items = banner.items.slice(midPoint);
            } else {
              // En horizontal: 3 columnas como antes
              column1Items = banner.items.slice(0, 6); // Primeros 6 items
              column2Items = banner.items.slice(6, 12); // Items 7-12
            }
            
            // Función auxiliar para renderizar un item (similar a renderExtraItem)
            const renderFreebieItem = (item: typeof banner.items[0]) => (
              <div
                key={item.id}
                className="flex items-center gap-2"
                style={{
                  padding: '2px 4px'
                }}
              >
                {/* Columna 1: Artículo */}
                <div className="flex items-center gap-2 flex-1 min-w-0">
                  {item.show_image === 1 && item.image_url && (
                    <img
                      src={item.image_url}
                      alt={item.name}
                      className="w-12 h-16 object-cover rounded flex-shrink-0"
                    />
                  )}
                  <p
                    className="font-normal truncate"
                    style={{
                      color: '#ffffff',
                      fontSize: `${banner.items_config?.text_font_size || (data.screen.fonts?.text?.size || 18)}px`,
                      fontFamily: banner.items_config?.text_font 
                        ? `'${cleanFontFamily(banner.items_config.text_font.family)}'` 
                        : (data.screen.fonts?.text?.family ? `'${cleanFontFamily(data.screen.fonts.text.family)}'` : 'inherit'),
                      margin: 0,
                      lineHeight: isVertical ? '1.0' : '0.8',  // Mayor line-height en vertical
                      textTransform: 'none'
                    }}
                  >
                    {item.name}
                  </p>
                </div>
                
                {/* Columna 2: Precio con fondo negro */}
                {item.show_price === 1 && item.price !== null && item.price !== undefined && (
                  <div
                    style={{
                      backgroundColor: '#000000',
                      padding: '2px 6px',
                      borderRadius: '4px',
                      flexShrink: 0
                    }}
                  >
                    <p
                      className="font-normal"
                      style={{
                        color: '#ffffff',
                        fontSize: `${banner.items_config?.price_font_size || (data.screen.fonts?.price_number?.size || data.screen.fonts?.text?.size || 20)}px`,
                        fontFamily: banner.items_config?.price_font 
                          ? `'${cleanFontFamily(banner.items_config.price_font.family)}'` 
                          : (data.screen.fonts?.price_number?.family 
                              ? `'${cleanFontFamily(data.screen.fonts.price_number.family)}'` 
                              : (data.screen.fonts?.text?.family ? `'${cleanFontFamily(data.screen.fonts.text.family)}'` : 'inherit')),
                        margin: 0,
                        lineHeight: isVertical ? '1.0' : '0.9',  // Mayor line-height en vertical
                        whiteSpace: 'nowrap'
                      }}
                    >
                      +${Math.round(typeof item.price === 'number' ? item.price : parseFloat(String(item.price || '0')))}
                    </p>
                  </div>
                )}
              </div>
            );
            
            if (isVertical) {
              // Layout vertical: 2 columnas arriba, 1 columna abajo para leyenda
              return (
                <div className="flex flex-col h-full w-full" style={{ gap: '4px', padding: isLateral ? '2px 48px' : '4px 48px' }}>
                  {/* Contenedor superior: 2 columnas con items */}
                  <div className="flex w-full" style={{ gap: '4px', flex: 1 }}>
                    {/* Columna 1 */}
                    <div className="flex flex-col flex-1" style={{ width: '50%', gap: '4px' }}>
                      <div className="flex flex-col" style={{ gap: '2px', flex: 1, overflow: 'auto' }}>
                        {column1Items.map(renderFreebieItem)}
                      </div>
                    </div>
                    
                    {/* Columna 2 */}
                    <div className="flex flex-col flex-1" style={{ width: '50%', gap: '4px' }}>
                      <div className="flex flex-col" style={{ gap: '2px', flex: 1, overflow: 'auto' }}>
                        {column2Items.map(renderFreebieItem)}
                      </div>
                    </div>
                  </div>
                  
                  {/* Contenedor inferior: 1 columna completa para leyenda */}
                  <div className="flex flex-col w-full" style={{ gap: '4px', flexShrink: 0 }}>
                    <div 
                      style={{
                        padding: '10px',
                        textAlign: 'center',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: '100%',
                        overflow: 'visible'
                      }}
                    >
                      <p
                        style={{
                          color: '#ffffff',
                          backgroundColor: '#FF6B35',
                          padding: '8px 12px',
                          borderRadius: '4px',
                          fontSize: `${banner.items_config?.text_font_size || (data.screen.fonts?.text?.size || 18)}px`,
                          fontFamily: banner.items_config?.text_font 
                            ? `'${cleanFontFamily(banner.items_config.text_font.family)}'` 
                            : (data.screen.fonts?.text?.family ? `'${cleanFontFamily(data.screen.fonts.text.family)}'` : 'inherit'),
                          margin: 0,
                          fontWeight: 'normal',
                          lineHeight: '1.1',
                          display: 'block',
                          width: '100%',
                          maxWidth: '100%',
                          minWidth: '100%',
                          boxSizing: 'border-box',
                          overflow: 'visible',
                          wordWrap: 'break-word',
                          hyphens: 'auto'
                        }}
                      >
                        Agrega a cualquier sandwich nuestro Spicy Jimmy Peppers y dale un toque picosito.
                      </p>
                    </div>
                  </div>
                </div>
              );
            }
            
            // Layout horizontal: 3 columnas como antes
            return (
              <div className="flex h-full w-full" style={{ gap: '4px', padding: isLateral ? '2px 48px' : '4px 48px' }}>
                {/* Columna 1: 6 productos */}
                <div className="flex flex-col flex-1" style={{ width: '33.333%', gap: '4px' }}>
                  <div className="flex flex-col" style={{ gap: '2px', flex: 1, overflow: 'auto' }}>
                    {column1Items.map(renderFreebieItem)}
                  </div>
                </div>
                
                {/* Columna 2: 6 productos */}
                <div className="flex flex-col flex-1" style={{ width: '33.333%', gap: '4px' }}>
                  <div className="flex flex-col" style={{ gap: '2px', flex: 1, overflow: 'auto' }}>
                    {column2Items.map(renderFreebieItem)}
                  </div>
                </div>
                
                {/* Columna 3: Leyenda personalizable */}
                <div className="flex flex-col flex-1" style={{ width: '33.333%', gap: '4px' }}>
                  <div 
                    style={{
                      padding: '10px',
                      textAlign: 'center',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flex: 1,
                      minHeight: '100px',
                      width: '100%',
                      overflow: 'visible'
                    }}
                  >
                    <p
                      style={{
                        color: '#ffffff',
                        backgroundColor: '#FF6B35',
                        padding: '8px 12px',
                        borderRadius: '4px',
                        fontSize: `${banner.items_config?.text_font_size || (data.screen.fonts?.text?.size || 18)}px`,
                        fontFamily: banner.items_config?.text_font 
                          ? `'${cleanFontFamily(banner.items_config.text_font.family)}'` 
                          : (data.screen.fonts?.text?.family ? `'${cleanFontFamily(data.screen.fonts.text.family)}'` : 'inherit'),
                        margin: 0,
                        fontWeight: 'normal',
                        lineHeight: '1.1',
                        display: 'block',
                        width: '100%',
                        maxWidth: '100%',
                        minWidth: '100%',
                        boxSizing: 'border-box',
                        overflow: 'visible',
                        wordWrap: 'break-word',
                        hyphens: 'auto'
                      }}
                    >
                      Agrega a cualquier sandwich nuestro Spicy Jimmy Peppers y dale un toque picosito.
                    </p>
                  </div>
                </div>
              </div>
            );
          }
          
          // Para otros tipos, mantener el comportamiento actual
          return (
            <div
              className="flex-1 overflow-hidden"
              style={{
                display: 'grid',
                gridTemplateColumns: banner.columns === 2 ? 'repeat(2, 1fr)' : '1fr',
                gap: '0px',
                padding: isLateral ? '4px 20px' : '8px 12px', // Padding horizontal aumentado para laterales
                // Calcular altura fija por fila basada en el tamaño de fuente
                gridAutoRows: (() => {
                  const baseFontSize = banner.items_config?.text_font_size || (data.screen.fonts?.text?.size || 18);
                  const fontSize = baseFontSize; // Usar el valor configurado directamente
                  // Altura = fontSize (con lineHeight 1.2) + padding para separación vertical
                  return isLateral ? `${fontSize + 20}px` : `${fontSize + 22}px`; // Aumentado para más separación vertical
                })(),
                alignItems: 'center', // Centrar verticalmente el contenido en cada celda
                alignContent: 'center' // Centrar el contenido del grid verticalmente
              }}
            >
              {!isPostresType && banner.items.map((item, itemIndex) => {
                // Determinar si este item está en la primera columna (necesita borde derecho para dividir columnas)
                const isFirstColumn = banner.columns === 2 && itemIndex % 2 === 0;
                const needsDivider = isFirstColumn && isBebidasPostres; // Removido isExtrasType ya que se maneja antes
              
              // Para otros tipos: layout normal (precio después del nombre)
              return (
                <div
                  key={item.id}
                  className="flex items-center justify-center gap-2"
                  style={{
                    padding: isLateral ? '0px 4px' : '4px 4px',
                    borderRight: needsDivider ? '2px solid #ffffff' : 'none',
                    paddingRight: needsDivider ? '8px' : (isLateral ? '4px' : '4px'),
                    marginRight: needsDivider ? '8px' : '0'
                  }}
                >
                  <div className="flex items-center gap-2 flex-1 min-w-0">
                    {item.show_image === 1 && item.image_url && (
                      <img
                        src={item.image_url}
                        alt={item.name}
                        className="w-8 h-8 object-cover rounded flex-shrink-0"
                      />
                    )}
                    <p
                      className="font-semibold truncate"
                      style={{
                        color: isExtrasType ? '#ffffff' : (banner.items_config?.text_color || data.screen.colors.text || '#000000'),
                        fontSize: `${banner.items_config?.text_font_size || (data.screen.fonts?.text?.size || 18)}px`,
                        fontFamily: banner.items_config?.text_font 
                          ? `'${cleanFontFamily(banner.items_config.text_font.family)}'` 
                          : (data.screen.fonts?.text?.family ? `'${cleanFontFamily(data.screen.fonts.text.family)}'` : 'inherit'),
                        margin: 0,
                        lineHeight: '1.2',
                        textTransform: 'uppercase'
                      }}
                    >
                      {item.name}
                    </p>
                  </div>
                  {item.show_price === 1 && item.price !== null && item.price !== undefined && (
                    <p
                      className="font-bold flex-shrink-0"
                      style={{
                        color: isExtrasType ? '#ffffff' : (banner.items_config?.price_color || data.screen.colors.price_number || data.screen.colors.text || '#000000'),
                        fontSize: `${banner.items_config?.price_font_size || (data.screen.fonts?.price_number?.size || data.screen.fonts?.text?.size || 20)}px`,
                        fontFamily: banner.items_config?.price_font 
                          ? `'${cleanFontFamily(banner.items_config.price_font.family)}'` 
                          : (data.screen.fonts?.price_number?.family 
                              ? `'${cleanFontFamily(data.screen.fonts.price_number.family)}'` 
                              : (data.screen.fonts?.text?.family ? `'${cleanFontFamily(data.screen.fonts.text.family)}'` : 'inherit')),
                        margin: 0,
                        lineHeight: '1.2'
                      }}
                    >
                      ${Math.round(typeof item.price === 'number' ? item.price : parseFloat(String(item.price || '0')))}
                    </p>
                  )}
                </div>
              );
            })}
          </div>
          );
        })()}
        
        {/* Para tipo 'combo': mostrar siempre, incluso sin items */}
        {isComboType && (() => {
          // Obtener precio regular de la tarifa
          const regularPrice = data?.extras?.precio_hazlo_combo_regular;
          
          if (isVertical) {
            // LAYOUT VERTICAL: Una sola columna centrada, texto primero, precio al final
            return (
              <div className="flex flex-col h-full w-full items-center justify-center" style={{ gap: '16px', padding: isLateral ? '2px 48px' : '4px 48px' }}>
                {/* Texto primero (centrado) */}
                <div className="flex flex-col items-center justify-center">
                  <p
                    style={{
                      color: '#ffffff',
                      fontSize: `${banner.items_config?.text_font_size || (data.screen.fonts?.text?.size || 18)}px`,
                      fontFamily: banner.items_config?.text_font 
                        ? `'${cleanFontFamily(banner.items_config.text_font.family)}'` 
                        : (data.screen.fonts?.text?.family ? `'${cleanFontFamily(data.screen.fonts.text.family)}'` : 'inherit'),
                      margin: 0,
                      fontWeight: 'normal',
                      lineHeight: '1.2',
                      textAlign: 'center'
                    }}
                  >
                    Incluye bebida, chips o postre
                  </p>
                </div>
                
                {/* Precio al final (centrado) */}
                {regularPrice && (
                  <div className="flex flex-col items-center justify-center">
                    <p
                      style={{
                        color: '#ffffff',
                        fontSize: `${banner.items_config?.price_font_size || (data.screen.fonts?.price_number?.size || data.screen.fonts?.text?.size || 20)}px`,
                        fontFamily: banner.items_config?.price_font 
                          ? `'${cleanFontFamily(banner.items_config.price_font.family)}'` 
                          : (data.screen.fonts?.price_number?.family 
                              ? `'${cleanFontFamily(data.screen.fonts.price_number.family)}'` 
                              : (data.screen.fonts?.text?.family ? `'${cleanFontFamily(data.screen.fonts.text.family)}'` : 'inherit')),
                        margin: 0,
                        fontWeight: 'bold',
                        lineHeight: '1.0',
                        textAlign: 'center'
                      }}
                    >
                      +${Math.round(parseFloat(regularPrice))}
                    </p>
                  </div>
                )}
              </div>
            );
          }
          
          // LAYOUT HORIZONTAL: Mantener el layout original de dos columnas (sin cambios)
          return (
            <div className="flex h-full w-full" style={{ gap: '8px', padding: isLateral ? '2px 48px' : '4px 48px' }}>
              {/* Columna 1 (50%): Leyenda */}
              <div className="flex flex-col flex-1" style={{ width: '50%', justifyContent: 'center' }}>
                <p
                  style={{
                    color: '#ffffff',
                    fontSize: `${banner.items_config?.text_font_size || (data.screen.fonts?.text?.size || 18)}px`,
                    fontFamily: banner.items_config?.text_font 
                      ? `'${cleanFontFamily(banner.items_config.text_font.family)}'` 
                      : (data.screen.fonts?.text?.family ? `'${cleanFontFamily(data.screen.fonts.text.family)}'` : 'inherit'),
                    margin: 0,
                    fontWeight: 'normal',
                    lineHeight: '1.2'
                  }}
                >
                  Incluye bebida, chips o postre
                </p>
              </div>
              
              {/* Columna 2 (50%): Precio */}
              <div className="flex flex-col flex-1" style={{ width: '50%', justifyContent: 'center', alignItems: 'flex-end' }}>
                {regularPrice && (
                  <p
                    style={{
                      color: '#ffffff',
                      fontSize: `${banner.items_config?.price_font_size || (data.screen.fonts?.price_number?.size || data.screen.fonts?.text?.size || 20)}px`,
                      fontFamily: banner.items_config?.price_font 
                        ? `'${cleanFontFamily(banner.items_config.price_font.family)}'` 
                        : (data.screen.fonts?.price_number?.family 
                            ? `'${cleanFontFamily(data.screen.fonts.price_number.family)}'` 
                            : (data.screen.fonts?.text?.family ? `'${cleanFontFamily(data.screen.fonts.text.family)}'` : 'inherit')),
                      margin: 0,
                      fontWeight: 'bold',
                      lineHeight: '1.0'
                    }}
                  >
                    +${Math.round(parseFloat(regularPrice))}
                  </p>
                )}
              </div>
            </div>
          );
        })()}
        
        {/* Para tipo 'postres': mostrar siempre, incluso sin items */}
        {isPostresType && (() => {
          // Log para depuración
          console.log('🍰 [DisplayScreen] Renderizando Banner Postres:', {
            banner_id: banner.id,
            banner_name: banner.name,
            items_config: banner.items_config,
            items_config_full: JSON.stringify(banner.items_config, null, 2), // Objeto completo expandido
            // Mostrar información de depuración del backend si existe
            debug_info: (banner as any)._debug || 'No disponible',
            debug_info_full: JSON.stringify((banner as any)._debug || {}, null, 2),
            text_font_size: banner.items_config?.text_font_size,
            price_font_size: banner.items_config?.price_font_size,
            text_color: banner.items_config?.text_color,
            price_color: banner.items_config?.price_color,
            screen_fonts: data.screen.fonts,
            screen_colors: data.screen.colors,
            // Mostrar todo el objeto del banner
            full_banner_object: banner
          });
          
          // Log específico para debug_info expandido
          if ((banner as any)._debug) {
            console.log('🔍 [DisplayScreen] DEBUG INFO EXPANDIDO para Banner Postres:', {
              raw_from_db: (banner as any)._debug.raw_from_db,
              processed: (banner as any)._debug.processed,
              hasItemConfigForThisBanner: (banner as any)._debug.hasItemConfigForThisBanner,
              final_items_config: (banner as any)._debug.final_items_config,
              comparacion: {
                'BD item_text_color': (banner as any)._debug.raw_from_db?.item_text_color,
                'BD item_price_color': (banner as any)._debug.raw_from_db?.item_price_color,
                'Processed itemTextColor': (banner as any)._debug.processed?.itemTextColor,
                'Processed itemPriceColor': (banner as any)._debug.processed?.itemPriceColor,
                'Final text_color': (banner as any)._debug.final_items_config?.text_color,
                'Final price_color': (banner as any)._debug.final_items_config?.price_color
              }
            });
          }
          
          // Log adicional para ver qué valores se están usando en el render
          console.log('🎨 [DisplayScreen] Valores que se usarán en el render del Banner Postres:', {
            text_color_aplicado: banner.items_config?.text_color || data.screen.colors.text || '#000000',
            price_color_aplicado: banner.items_config?.price_color || data.screen.colors.price_number || data.screen.colors.text || '#000000',
            text_font_size_aplicado: banner.items_config?.text_font_size || (data.screen.fonts?.text?.size || 18),
            price_font_size_aplicado: banner.items_config?.price_font_size || (data.screen.fonts?.price_number?.size || data.screen.fonts?.text?.size || 20),
            tiene_items_config: !!banner.items_config,
            items_config_existe: banner.items_config !== undefined && banner.items_config !== null
          });
          
          // Obtener precios de la tarifa
          const precioCookie = data?.extras?.precio_cookie;
          const precioBrownie = data?.extras?.precio_brownie;
          
          // Obtener items de postres por display_order: 1 = Galleta, 2 = Brownie
          const itemGalleta = banner.items.find(item => (item.display_order ?? 0) === 1);
          const itemBrownie = banner.items.find(item => (item.display_order ?? 0) === 2);
          
          // Obtener imágenes de los items (si existen y show_image === 1)
          const imagenGalleta = itemGalleta?.show_image === 1 && itemGalleta?.image_url ? itemGalleta.image_url : null;
          const imagenBrownie = itemBrownie?.show_image === 1 && itemBrownie?.image_url ? itemBrownie.image_url : null;
          
          // Layout vertical: solo GALLETA y BROWNIE con imágenes a la derecha
          if (isVertical) {
            // Función auxiliar para renderizar un postre en vertical
            const renderPostreVertical = (titulo: string, precio: string | null, imagenUrl: string | null = null) => (
              <div className="flex items-center justify-center" style={{ gap: '12px', marginBottom: '16px' }}>
                {/* Columna izquierda: Nombre y Precio */}
                <div className="flex flex-col items-start justify-center" style={{ gap: '4px', flex: 1, width: '100%' }}>
                  {/* Título con fondo negro */}
                  <div style={{ textAlign: 'left', width: '100%' }}>
                    <p
                      style={{
                        color: '#ffffff',
                        backgroundColor: '#000000',
                        padding: '4px 8px',
                        borderRadius: '0px',
                        fontSize: `${banner.items_config?.text_font_size || (data.screen.fonts?.text?.size || 18)}px`,
                        fontFamily: banner.items_config?.text_font 
                          ? `'${cleanFontFamily(banner.items_config.text_font.family)}'` 
                          : (data.screen.fonts?.text?.family ? `'${cleanFontFamily(data.screen.fonts.text.family)}'` : 'inherit'),
                        margin: 0,
                        fontWeight: 'bold',
                        lineHeight: '1.0',
                        display: 'inline-block',
                        textAlign: 'left'
                      }}
                    >
                      {titulo}
                    </p>
                  </div>
                  
                  {/* Precio */}
                  {precio && (
                    <div style={{ textAlign: 'left', width: '100%' }}>
                      <p
                        style={{
                          color: banner.items_config?.price_color || data.screen.colors.price_number || data.screen.colors.text || '#ffffff',
                          fontSize: `${banner.items_config?.price_font_size || (data.screen.fonts?.price_number?.size || data.screen.fonts?.text?.size || 20)}px`,
                          fontFamily: banner.items_config?.price_font 
                            ? `'${cleanFontFamily(banner.items_config.price_font.family)}'` 
                            : (data.screen.fonts?.price_number?.family 
                                ? `'${cleanFontFamily(data.screen.fonts.price_number.family)}'` 
                                : (data.screen.fonts?.text?.family ? `'${cleanFontFamily(data.screen.fonts.text.family)}'` : 'inherit')),
                          margin: 0,
                          fontWeight: 'bold',
                          lineHeight: '1.0',
                          textAlign: 'left'
                        }}
                      >
                        ${Math.round(parseFloat(precio))}
                      </p>
                    </div>
                  )}
                </div>
                
                {/* Columna derecha: Imagen */}
                {imagenUrl && (
                  <div className="flex items-center justify-center" style={{ width: '180px', flexShrink: 0, height: '100%' }}>
                    <img
                      src={imagenUrl}
                      alt={titulo}
                      className="object-contain"
                      style={{
                        maxHeight: '120%',  // Aumentado para hacer la imagen más grande
                        maxWidth: '120%',   // Aumentado para hacer la imagen más grande
                        height: 'auto',
                        width: 'auto',
                        objectFit: 'contain'
                      }}
                    />
                  </div>
                )}
              </div>
            );
            
            // Usar nombres personalizados de los items si existen, o los nombres por defecto
            const nombreGalleta = itemGalleta?.name || 'GALLETA';
            const nombreBrownie = itemBrownie?.name || 'BROWNIE';
            
            return (
              <div className="flex flex-col h-full w-full items-center" style={{ gap: '8px', padding: isLateral ? '2px 48px' : '4px 48px', justifyContent: 'center' }}>
                {renderPostreVertical(nombreGalleta.toUpperCase(), precioCookie ?? null, imagenGalleta)}
                {renderPostreVertical(nombreBrownie.toUpperCase(), precioBrownie ?? null, imagenBrownie)}
              </div>
            );
          }
          
          // Layout horizontal: 3 columnas. display_order: 1 = AGUA, 2 = REGULAR (chips), 3 = GALLETA, 4 = BROWNIE
          const precioRegular = data?.extras?.precio_refresco_regular;
          const precioGrande = data?.extras?.precio_refresco_large;
          const precioAgua = data?.extras?.precio_refresco_bottle;
          const precioChips = data?.extras?.precio_chips;
          
          const imagenAgua = banner.items.find(item => (item.display_order ?? 0) === 1)?.image_url || null;
          const imagenChips = banner.items.find(item => (item.display_order ?? 0) === 2)?.image_url || null;
          const itemGalletaCol3 = banner.items.find(item => (item.display_order ?? 0) === 3);
          const itemBrownieCol3 = banner.items.find(item => (item.display_order ?? 0) === 4);
          const imagenGalletaCol3 = itemGalletaCol3?.show_image === 1 && itemGalletaCol3?.image_url ? itemGalletaCol3.image_url : null;
          const imagenBrownieCol3 = itemBrownieCol3?.show_image === 1 && itemBrownieCol3?.image_url ? itemBrownieCol3.image_url : null;
          
          // Función auxiliar para renderizar un elemento con título y precio
          const renderPostreItem = (titulo: string, precio: string | null, tieneImagen: boolean = false, imagenUrl: string | null = null, maxImageHeight: number = 120, imagenAlLado: boolean = false) => (
            <div className="flex" style={{ gap: '8px', marginBottom: '12px', flexDirection: imagenAlLado ? 'row' : 'column', alignItems: imagenAlLado ? 'flex-start' : 'flex-start' }}>
              {/* Contenedor izquierdo: Título y Precio */}
              <div className="flex flex-col" style={{ gap: '4px', flex: imagenAlLado ? 1 : 'none' }}>
                {/* Título con fondo negro - SIEMPRE BLANCO para legibilidad */}
                <div style={{ textAlign: 'left' }}>
                  <p
                    style={{
                      color: '#ffffff', // Siempre blanco porque tiene fondo negro
                      backgroundColor: '#000000',
                      padding: '4px 8px',
                      borderRadius: '0px', // Sin border-radius
                      fontSize: `${banner.items_config?.text_font_size || (data.screen.fonts?.text?.size || 18)}px`,
                      fontFamily: banner.items_config?.text_font 
                        ? `'${cleanFontFamily(banner.items_config.text_font.family)}'` 
                        : (data.screen.fonts?.text?.family ? `'${cleanFontFamily(data.screen.fonts.text.family)}'` : 'inherit'),
                      margin: 0,
                      fontWeight: 'bold',
                      lineHeight: '1.0',
                      display: 'inline-block'
                    }}
                  >
                    {titulo}
                  </p>
                </div>
                
                {/* Precio */}
                {precio && (() => {
                  const precioColorFinal = banner.items_config?.price_color || data.screen.colors.price_number || data.screen.colors.text || '#000000';
                  const precioSizeFinal = banner.items_config?.price_font_size || (data.screen.fonts?.price_number?.size || data.screen.fonts?.text?.size || 20);
                  
                  // Log para depuración del precio
                  if (banner.id === 39) { // Solo para banner Postres
                    console.log('💰 [DisplayScreen] Renderizando precio Postres:', {
                      titulo,
                      precio,
                      precioColorFinal,
                      precioSizeFinal,
                      items_config_price_color: banner.items_config?.price_color,
                      items_config_price_font_size: banner.items_config?.price_font_size,
                      screen_colors_price_number: data.screen.colors.price_number,
                      screen_colors_text: data.screen.colors.text,
                      screen_fonts_price_number_size: data.screen.fonts?.price_number?.size,
                      screen_fonts_text_size: data.screen.fonts?.text?.size
                    });
                  }
                  
                  return (
                  <div style={{ textAlign: 'left' }}>
                    <p
                      style={{
                        color: precioColorFinal,
                        fontSize: `${precioSizeFinal}px`,
                        fontFamily: banner.items_config?.price_font 
                          ? `'${cleanFontFamily(banner.items_config.price_font.family)}'` 
                          : (data.screen.fonts?.price_number?.family 
                              ? `'${cleanFontFamily(data.screen.fonts.price_number.family)}'` 
                              : (data.screen.fonts?.text?.family ? `'${cleanFontFamily(data.screen.fonts.text.family)}'` : 'inherit')),
                        margin: 0,
                        fontWeight: 'bold',
                        lineHeight: '1.0'
                      }}
                    >
                      ${Math.round(parseFloat(precio))}
                    </p>
                  </div>
                  );
                })()}
              </div>
              
              {/* Imagen (si aplica) */}
              {tieneImagen && imagenUrl && (
                <div style={{ textAlign: imagenAlLado ? 'right' : 'left', marginTop: imagenAlLado ? '0' : '8px', alignSelf: imagenAlLado ? 'flex-start' : 'stretch' }}>
                  <img
                    src={imagenUrl}
                    alt={titulo}
                    className="object-contain"
                    style={{
                      maxHeight: `${maxImageHeight}px`, // Usar el parámetro para el tamaño
                      maxWidth: imagenAlLado ? '150px' : '100%',
                      height: 'auto',
                      width: 'auto'
                    }}
                  />
                </div>
              )}
            </div>
          );
          
          return (
            <div className="flex h-full w-full" style={{ gap: '8px', padding: isLateral ? '2px 48px' : '4px 48px' }}>
              {/* Columna 1: Bebidas */}
              <div className="flex flex-col flex-1" style={{ width: '33.333%', justifyContent: 'flex-start', paddingTop: '8px' }}>
                {renderPostreItem('REGULAR 473ml', precioRegular ?? null)}
                {renderPostreItem('GRANDE 650ml', precioGrande ?? null)}
                {renderPostreItem('AGUA', precioAgua ?? null, true, imagenAgua)}
              </div>
              
              {/* Columna 2: Chips */}
              <div className="flex flex-col flex-1" style={{ width: '33.333%', justifyContent: 'flex-start', paddingTop: '8px' }}>
                {renderPostreItem('REGULAR', precioChips ?? null, true, imagenChips, 300)} {/* 300px para Chips (aún más grande) */}
              </div>
              
              {/* Columna 3: Postres (display_order 3 = GALLETA, 4 = BROWNIE) */}
              <div className="flex flex-col flex-1" style={{ width: '33.333%', justifyContent: 'flex-start', paddingTop: '8px' }}>
                {renderPostreItem('GALLETA', precioCookie ?? null, true, imagenGalletaCol3, 80, true)}
                {renderPostreItem('BROWNIE', precioBrownie ?? null, true, imagenBrownieCol3, 80, true)}
              </div>
            </div>
          );
        })()}
      </div>
    );
  };

  const renderBannerSection = (size: 'side' | 'full' = 'side') => {
    if (!data) return null;
    const { banners, smart_banners, screen } = data;
    
    // No mostrar banners laterales si es solo banners
    if (screen.content_type === 'banners_only') {
      return null;
    }
    
    const lateralSlots = screen.banner_divisions || 2;
    const lateralItems = banners.laterales.slice(0, lateralSlots);
    const smartLateralItems = smart_banners?.laterales || [];

    // Log temporal para depurar
    console.log('🎨 [renderBannerSection]', {
      lateralSlots,
      lateralItemsCount: lateralItems.length,
      smartLateralItemsCount: smartLateralItems.length,
      smartLateralItems: smartLateralItems,
      banners_laterales: banners.laterales
    });

    // Combinar banners normales y smart banners laterales
    const allLateralItems = [
      ...lateralItems.map(b => ({ type: 'normal' as const, banner: b })),
      ...smartLateralItems.map(b => ({ type: 'smart' as const, banner: b }))
    ].sort((a, b) => {
      const posA = a.banner.lateral_position || 0;
      const posB = b.banner.lateral_position || 0;
      return posA - posB;
    });

    // Solo renderizar banners laterales si es modo 'side'
    if (size === 'side') {
      if (!allLateralItems.length) {
        return (
          <div className="flex items-center justify-center h-full w-full rounded-3xl border border-dashed border-white/30 p-6 text-white/70 overflow-hidden">
            No hay banners configurados
          </div>
        );
      }

      return (
        <div 
          className="flex flex-col h-full w-full overflow-hidden"
          style={{ 
            gap: '0px'
          }}
        >
          {allLateralItems.slice(0, lateralSlots).map((item, index) => {
            // Para banners normales: distribución original (66.666% y 33.333%)
            // Para smart banners: distribución dinámica según cantidad
            let heightPercent: string;
            
            if (item.type === 'smart') {
              // Calcular altura dinámicamente solo para smart banners
              const totalBanners = allLateralItems.slice(0, lateralSlots).length;
              
              if (totalBanners === 2) {
                // 2 banners: primero 60%, segundo 40%
                heightPercent = item.banner.lateral_position === 1 ? '60%' : '40%';
              } else if (totalBanners === 3) {
                // 3 banners: primero 30%, segundo 20%, tercero 20% (30% vacío)
                if (item.banner.lateral_position === 1) {
                  heightPercent = '30%';
                } else if (item.banner.lateral_position === 2) {
                  heightPercent = '20%';
                } else {
                  heightPercent = '20%';
                }
              } else if (totalBanners === 4) {
                // 4 banners: 30%, 20%, 15%, 35%
                if (item.banner.lateral_position === 1) {
                  heightPercent = '30%';
                } else if (item.banner.lateral_position === 2) {
                  heightPercent = '20%';
                } else if (item.banner.lateral_position === 3) {
                  heightPercent = '15%';
                } else {
                  heightPercent = '35%';
                }
              } else {
                // Por defecto: distribución equitativa si hay más de 4
                heightPercent = `${100 / totalBanners}%`;
              }
            } else {
              // Banners normales: distribución original
              heightPercent = item.banner.lateral_position === 1 ? '66.666%' : '33.333%';
            }
            
            if (item.type === 'smart') {
              return (
                <div 
                  key={`smart-lateral-${item.banner.id}-${index}`} 
                  className="w-full overflow-hidden"
                  style={{ 
                    flex: `0 0 ${heightPercent}`,
                    height: heightPercent,
                    minHeight: heightPercent,
                    maxHeight: heightPercent
                  }}
                >
                  {renderSmartBanner(item.banner as SmartBannerItem, true)}
                </div>
              );
            }
            
            return (
              <div 
                key={`lateral-${item.banner.id}-${index}`} 
                className="overflow-hidden shadow-xl bg-black/10 flex-shrink-0 w-full"
                style={{ height: heightPercent }}
              >
                {item.banner.image_url ? (
                  <img 
                    src={item.banner.image_url} 
                    alt={item.banner.name} 
                    className="w-full h-full object-cover" 
                  />
                ) : (
                  <div className="h-full w-full flex items-center justify-center text-white/70">Banner sin imagen</div>
                )}
              </div>
            );
          })}
        </div>
      );
    }

    // Modo 'full' - solo banners laterales (el superior se renderiza aparte)
    if (!lateralItems.length) {
      return null;
    }

    return (
      <div 
        className="grid h-full w-full overflow-hidden"
        style={{
          gridTemplateColumns: 'repeat(2, 1fr)',
          gap: '0px'
        }}
      >
        {lateralItems.map((banner, index) => (
          <div 
            key={`lateral-${banner.id}-${index}`} 
            className="overflow-hidden shadow-xl bg-black/10 h-full"
          >
            {banner.image_url ? (
              <img 
                src={banner.image_url} 
                alt={banner.name} 
                className="w-full h-full object-cover" 
              />
            ) : (
              <div className="h-full w-full flex items-center justify-center text-white/70">Banner sin imagen</div>
            )}
          </div>
        ))}
      </div>
    );
  };

  const renderProducts = () => {
    if (!data || !data.products.references.length) {
      return (
        <div className="text-center text-white/70 bg-white/10 rounded-3xl p-12 border border-white/20 h-full w-full flex items-center justify-center overflow-hidden">
          No hay productos configurados para esta pantalla
        </div>
      );
    }

    // Obtener tamaños de fuente de la configuración de la pantalla
    const titleFontSize = data.screen.fonts?.title?.size || 24;
    const subtitleFontSize = data.screen.fonts?.subtitle?.size || 18;
    const textFontSize = data.screen.fonts?.text?.size || 14;

    // Detectar si no hay banners laterales (para aumentar márgenes de cards)
    const hasNoLateralBanners = data.screen.banner_divisions === 0 || 
      ((data.banners?.laterales?.length || 0) === 0 && (data.smart_banners?.laterales?.length || 0) === 0);

    // Calcular grid dinámico según products_count y orientation
    const productsCount = data.screen.products_count || 10;
    const isVertical = data.screen.orientation === 'vertical';
    
    // En vertical, usar el tamaño exacto configurado; en horizontal, mantener el multiplicador
    const finalTitleFontSize = isVertical ? titleFontSize : titleFontSize * 1.3;
    const finalSubtitleFontSize = isVertical ? subtitleFontSize : subtitleFontSize;
    const finalTextFontSize = isVertical ? textFontSize : textFontSize;
    let gridColumns = 5;
    let gridRows = 2;

    if (isVertical) {
      // En vertical: 2 columnas x 3 filas para 6 productos
      gridColumns = 2;
      gridRows = 3;
    } else if (productsCount === 6) {
      gridColumns = 3;
      gridRows = 2;
    } else if (productsCount === 8) {
      gridColumns = 4;
      gridRows = 2;
    } else if (productsCount === 10) {
      gridColumns = 5;
      gridRows = 2;
    } else if (productsCount === 12) {
      gridColumns = 6;
      gridRows = 2;
    }

    return (
      <div 
        className={`grid h-full w-full overflow-hidden ${isVertical ? 'gap-3' : 'gap-1'}`}
        style={{
          gridTemplateColumns: `repeat(${gridColumns}, 1fr)`,
          gridTemplateRows: `repeat(${gridRows}, 1fr)`,
          gridAutoFlow: 'row'
        }}
      >
        {data.products.references.map((reference, index) => {
          // Obtener la primera imagen disponible de las variantes
          const productImage = reference.variants.find(v => v.image_url)?.image_url || null;
          
          // Verificar si alguna variante tiene aguacate
          // El backend garantiza que el valor sea 0 o 1 (number)
          const hasAvocado = reference.variants.some(v => v.tiene_aguacate === 1);
          
          // Verificar si alguna variante es picante
          const hasSpicy = reference.variants.some(v => v.es_picante === 1);
          
          // Verificar si alguna variante es recomendada
          const hasRecommended = reference.variants.some(v => v.es_recomendado === 1);
          
          // Filtrar solo Regular y Gigante (excluir Combo y Combo Grande)
          const regularVariant = reference.variants.find(v => {
            const label = v.variant_label.toLowerCase();
            return label.includes('regular') && !label.includes('combo');
          });
          
          const giganteVariant = reference.variants.find(v => {
            const label = v.variant_label.toLowerCase();
            return (label.includes('gigante') || label.includes('grande')) && !label.includes('combo');
          });
          
          return (
            <div
              key={`${reference.reference_code || reference.base_name}-${index}`}
              className="flex flex-col items-center justify-start text-center overflow-hidden"
              style={{
                minWidth: 0,
                minHeight: 0,
                height: '100%',
                // En vertical, aumentar padding vertical pero reducir horizontal; en horizontal, mantener el original
                padding: isVertical 
                  ? (hasNoLateralBanners ? '8px 16px' : '8px 8px')
                  : (hasNoLateralBanners ? '2px 32px' : '2px'),
                // Borde horizontal divisor entre filas (solo vertical, sin top ni bottom)
                ...(isVertical && index >= gridColumns ? {
                  borderTop: '1px solid rgba(255, 255, 255, 0.2)'
                } : {})
              }}
            >
              {/* Título */}
              <div
                className={`font-bold tracking-tight overflow-hidden ${isVertical ? 'mb-1' : 'mb-0.5'}`}
                style={{
                  minHeight: isVertical ? `${finalTitleFontSize * 1}px` : `${finalTitleFontSize * 2}px`,
                  maxHeight: isVertical ? `${finalTitleFontSize * 1}px` : `${finalTitleFontSize * 2}px`,
                  display: 'flex',
                  alignItems: 'flex-end',
                  justifyContent: 'center',
                  textAlign: 'center'
                }}
              >
                <span
                  style={{
                    color: data.screen.colors.title || data.screen.colors.text || '#000000',
                    fontFamily: cleanFontFamily(data.screen.fonts?.title?.family) || 'GoodStuff-Titles',
                    fontSize: `${finalTitleFontSize}px`,
                    lineHeight: '1',
                    display: '-webkit-box',
                    WebkitLineClamp: isVertical ? 1 : 2,
                    WebkitBoxOrient: 'vertical',
                    fontWeight: '900',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis'
                  }}
                >
                  {/* Icono de aguacate */}
                  {hasAvocado && data.icons?.aguacate?.image_url && (
                    <img 
                      src={data.icons.aguacate.image_url} 
                      alt="Aguacate" 
                      style={{ 
                        display: 'inline-block', 
                        width: `${finalTitleFontSize}px`, 
                        height: `${finalTitleFontSize}px`,
                        verticalAlign: 'middle',
                        marginRight: '4px'
                      }} 
                    />
                  )}
                  {hasAvocado && !data.icons?.aguacate?.image_url && '🥑 '}
                  
                  {/* Icono de picante */}
                  {hasSpicy && data.icons?.picante?.image_url && (
                    <img 
                      src={data.icons.picante.image_url} 
                      alt="Picante" 
                      style={{ 
                        display: 'inline-block', 
                        width: `${finalTitleFontSize}px`, 
                        height: `${finalTitleFontSize}px`,
                        verticalAlign: 'middle',
                        marginRight: '4px'
                      }} 
                    />
                  )}
                  {hasSpicy && !data.icons?.picante?.image_url && '🌶️ '}
                  
                  {reference.base_name}
                </span>
              </div>
              
              {/* Descripción */}
              {reference.description && (
                <div
                  className={`px-0 overflow-hidden ${isVertical ? 'mb-1' : 'mb-0.5'}`}
                  style={{
                    // En vertical, reducir más el espacio de la descripción; en horizontal, mantener el cálculo dinámico
                    minHeight: isVertical ? '140px' : `${finalTextFontSize * 0.9 * 1.1 * 5}px`,
                    maxHeight: isVertical ? '140px' : `${finalTextFontSize * 0.9 * 1.1 * 5}px`,
                    display: 'flex',
                    alignItems: 'flex-start',
                    justifyContent: 'center',
                    textAlign: 'center',
                    paddingTop: '4px',
                    paddingLeft: hasNoLateralBanners ? '48px' : '0px', // Solo cuando no hay banners laterales
                    paddingRight: hasNoLateralBanners ? '48px' : '0px', // Solo cuando no hay banners laterales
                    width: '100%', // Asegurar que el contenedor ocupe todo el ancho
                    boxSizing: 'border-box' // Incluir padding en el cálculo del ancho
                  }}
                >
                  <span
                    style={{
                      color: data.screen.colors.text || '#000000',
                      fontFamily: cleanFontFamily(data.screen.fonts?.text?.family) || 'GoodStuff-Text',
                      fontSize: `${finalTextFontSize * 0.9}px`,
                      lineHeight: '1.1',
                      wordWrap: 'break-word',
                      overflowWrap: 'break-word',
                      display: '-webkit-box',
                      WebkitLineClamp: 5,
                      WebkitBoxOrient: 'vertical',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      width: '100%', // Asegurar que el span respete el ancho del contenedor
                      maxWidth: '100%' // Limitar el ancho máximo
                    }}
                  >
                    {reference.description}
                  </span>
                </div>
              )}
              
              {/* Imagen del producto - contenedor horizontal */}
              {productImage && (
                <div 
                  className={`rounded-lg overflow-hidden w-full relative ${isVertical ? 'mb-2' : 'mb-1'}`}
                  style={{ 
                    // En vertical, quitar aspectRatio fijo para que las imágenes se adapten mejor; en horizontal, mantener el original
                    aspectRatio: isVertical ? undefined : '800 / 458',
                    width: '100%',
                    position: 'relative',
                    // En vertical, quitar maxHeight para permitir que las imágenes sean más grandes
                    maxHeight: isVertical ? 'none' : 'none',
                    // En vertical, alinear el contenido hacia arriba
                    display: isVertical ? 'flex' : undefined,
                    alignItems: isVertical ? 'flex-start' : undefined,
                    justifyContent: isVertical ? 'flex-start' : undefined
                  }}
                >
                  <img 
                    src={productImage} 
                    alt={reference.base_name} 
                    className="w-full h-full object-contain"
                    style={{
                      // En vertical, reducir scale y mover hacia arriba para eliminar espacio en blanco; en horizontal, mantener el zoom original
                      transform: isVertical 
                        ? 'scale(0.85) translateY(-15px)' 
                        : `scale(${hasNoLateralBanners ? '1.3' : '1.2'})`,
                      transformOrigin: 'center',
                      objectPosition: isVertical ? 'top' : 'bottom',  // Cambiar a 'top' en vertical para evitar cortes por abajo
                      width: '100%',
                      height: isVertical ? 'auto' : undefined
                    }}
                  />
                  
                  {/* Icono de recomendado - overlay en lado izquierdo, centrado verticalmente */}
                  {hasRecommended && data.icons?.recomendado?.image_url && (
                    <div
                      style={{
                        position: 'absolute',
                        left: '8px',
                        top: '30%',
                        transform: 'translateY(-50%)',
                        zIndex: 10,
                        width: `${isVertical ? finalTitleFontSize * 1.5 : finalTitleFontSize * 2.5}px`,
                        height: `${isVertical ? finalTitleFontSize * 1.5 : finalTitleFontSize * 2.5}px`,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}
                    >
                      <img 
                        src={data.icons.recomendado.image_url} 
                        alt="Recomendado" 
                        style={{ 
                          width: '100%',
                          height: '100%',
                          objectFit: 'contain',
                          filter: 'drop-shadow(0 2px 4px rgba(0, 0, 0, 0.3))'
                        }} 
                      />
                    </div>
                  )}
                  {hasRecommended && !data.icons?.recomendado?.image_url && (
                    <div
                      style={{
                        position: 'absolute',
                        left: '8px',
                        top: '30%',
                        transform: 'translateY(-50%)',
                        zIndex: 10,
                        fontSize: `${isVertical ? finalTitleFontSize * 1.5 : finalTitleFontSize * 2.5}px`,
                        lineHeight: 1
                      }}
                    >
                      <StarIcon isDark={isDarkBackground(data.screen.colors.background)} size={`${isVertical ? finalTitleFontSize * 1.5 : finalTitleFontSize * 2.5}px`} />
                    </div>
                  )}
                </div>
              )}
              
              {/* Precios en una sola línea - USANDO COLORES DE CONFIGURACIÓN */}
              <div 
                className="flex items-end justify-center gap-2 w-full flex-shrink-0"
                style={{
                  fontFamily: cleanFontFamily(data.screen.fonts?.subtitle?.family) || 'GoodStuff-Subtitles',
                  fontSize: '16px',
                  lineHeight: '1.2',
                  minHeight: '28px',
                  paddingTop: isVertical ? '8px' : '4px'
                }}
              >
                {regularVariant && (
                  <>
                    <span style={{ 
                      fontFamily: `'${cleanFontFamily(data.screen.fonts?.price_text?.family) || cleanFontFamily(data.screen.fonts?.script?.family) || 'GoodStuff-Script'}'`, 
                      color: data.screen.colors.price_text || data.screen.colors.subtitle || data.screen.colors.text || '#333333',
                      fontSize: `${data.screen.fonts?.price_text?.size || 16}px`
                    }}>Regular</span>
                    <span style={{ 
                      fontFamily: `'${cleanFontFamily(data.screen.fonts?.price_number?.family) || cleanFontFamily(data.screen.fonts?.subtitle?.family) || 'GoodStuff-Subtitles'}'`,
                      color: data.screen.colors.price_number || data.screen.colors.subtitle || data.screen.colors.text || '#333333',
                      fontWeight: 'bold',
                      fontSize: `${data.screen.fonts?.price_number?.size || 16}px`
                    }}>{formatPrice(regularVariant.price)}</span>
                  </>
                )}
                {regularVariant && giganteVariant && (
                  <span style={{ 
                    color: data.screen.colors.price_text || data.screen.colors.subtitle || data.screen.colors.text || '#333333',
                    fontSize: `${data.screen.fonts?.price_text?.size || 16}px`,
                    display: 'inline-flex',
                    alignItems: 'center',
                    verticalAlign: 'middle'
                  }}>
                    <StarIcon isDark={isDarkBackground(data.screen.colors.background)} size={`${data.screen.fonts?.price_text?.size || 16}px`} />
                  </span>
                )}
                {giganteVariant && (
                  <>
                    <span style={{ 
                      fontFamily: `'${cleanFontFamily(data.screen.fonts?.price_text?.family) || cleanFontFamily(data.screen.fonts?.script?.family) || 'GoodStuff-Script'}'`, 
                      color: data.screen.colors.price_text || data.screen.colors.subtitle || data.screen.colors.text || '#333333',
                      fontSize: `${data.screen.fonts?.price_text?.size || 16}px`
                    }}>Grande</span>
                    <span style={{ 
                      fontFamily: `'${cleanFontFamily(data.screen.fonts?.price_number?.family) || cleanFontFamily(data.screen.fonts?.subtitle?.family) || 'GoodStuff-Subtitles'}'`,
                      color: data.screen.colors.price_number || data.screen.colors.subtitle || data.screen.colors.text || '#333333',
                      fontWeight: 'bold',
                      fontSize: `${data.screen.fonts?.price_number?.size || 16}px`
                    }}>{formatPrice(giganteVariant.price)}</span>
                  </>
                )}
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  // Función helper para obtener estilos de fuente
  // Maneja valores undefined/null de forma segura
  const getFontStyle = (fontConfig: any, fontSize: number | undefined | null): React.CSSProperties => {
    // Validar y establecer tamaño por defecto
    const size = (fontSize && fontSize > 0) ? fontSize : 16;
    
    // Si no hay fontConfig o no tiene family, retornar solo el tamaño
    if (!fontConfig || !fontConfig.family) {
      return {
        fontSize: `${size}px`
      };
    }
    
    return {
      fontFamily: cleanFontFamily(fontConfig.family) || 'inherit',
      fontSize: `${size}px`
    };
  };

  // Componente para renderizar un solo combo banner (reutilizable)
  const ComboBannerContent: React.FC<{ 
    banner: NonNullable<DisplayResponse['combo_banners']>[0];
    isVertical?: boolean;
  }> = ({ banner, isVertical = false }) => {
    if (!banner || !data) return null;

    // Obtener precio de agrandar (Gigante)
    const gigantePrice = data?.extras?.precio_hazlo_combo_gigante || banner.producto_precio;

    return (
      <div 
        className="w-full h-full flex flex-col"
        style={{
          backgroundColor: banner.background_color || '#ffffff'
        }}
      >
        {/* Título con dos palabras - Hasta arriba, en dos columnas: primera arriba, segunda abajo */}
        <div className="flex justify-center px-6 pt-4" style={{ paddingBottom: '10px' }}>
          <div className="flex flex-col items-center" style={{ lineHeight: '0.8' }}>
            <h1 
              className="font-bold"
              style={getFontStyle(banner.title_font, banner.title_font_size || 48)}
            >
              <span style={{ color: banner.title_word1_color }}>
                {banner.title_word1}
              </span>
            </h1>
            <h1 
              className="font-bold"
              style={getFontStyle(banner.title_font, banner.title_font_size || 48)}
            >
              <span style={{ color: banner.title_word2_color }}>
                {banner.title_word2}
              </span>
            </h1>
          </div>
        </div>

        {/* Imagen del combo - Sin mucho espacio del título */}
        <div 
          className="flex-shrink-0 flex items-center justify-center"
          style={{
            width: '100%',
            height: '50%',
            paddingTop: '10px',
            paddingBottom: '0px',
            paddingLeft: '20px',
            paddingRight: '20px'
          }}
        >
          {banner.combo_image_url && (
            <img
              src={banner.combo_image_url}
              alt="Combo"
              className="w-full h-full object-contain"
            />
          )}
        </div>

        {/* Contenedor para todo lo que está debajo de la imagen */}
        <div className="flex-1 flex flex-col items-center justify-center w-full">
          {isVertical ? (
            // LAYOUT VERTICAL: Una sola columna centrada, texto primero, precio al final
            <div 
              className="flex flex-col items-center justify-center"
              style={{
                width: '100%',
                maxWidth: '100%',
                padding: '0 20px'
              }}
            >
              {/* Nombre del producto */}
              <div 
                className="text-center"
                style={{
                  paddingTop: '2px',
                  paddingBottom: '10px'
                }}
              >
                <h2 
                  className="font-bold inline-block" 
                  style={{
                    ...getFontStyle(banner.product_name_font, banner.product_name_font_size || 24),
                    color: data?.screen?.colors?.title || '#000000',
                    textTransform: 'uppercase',
                    backgroundColor: banner.product_name_background_color || 'transparent',
                    padding: banner.product_name_background_color ? '4px 12px' : '0',
                    borderRadius: banner.product_name_background_color ? '4px' : '0'
                  }}
                >
                  {banner.product_name || 'Producto'}
                </h2>
              </div>

              {/* Descripción centrada (texto primero) */}
              <div 
                className="flex flex-col items-center justify-center"
                style={{
                  marginBottom: '20px'
                }}
              >
                {/* Palabra "Incluye" arriba de la descripción */}
                <span 
                  style={{
                    ...getFontStyle(banner.legend_font, banner.legend_font_size || 16),
                    color: banner.legend_text_color || '#000000',
                    marginBottom: '8px'
                  }}
                >
                  Incluye
                </span>
                {banner.description && (
                  <p 
                    style={{
                      ...getFontStyle(banner.description_font, banner.description_font_size || 18),
                      color: banner.description_color || '#000000',
                      textTransform: 'uppercase',
                      textAlign: 'center',
                      lineHeight: '1.2'
                    }}
                  >
                    {banner.description}
                  </p>
                )}
              </div>

              {/* Precio centrado (al final) */}
              <div 
                className="flex flex-col items-center justify-center"
              >
                <span 
                  style={{
                    ...getFontStyle(banner.price_font, banner.price_size || 24),
                    color: banner.price_color,
                    fontWeight: 'bold'
                  }}
                >
                  {formatPrice(banner.producto_precio)}
                </span>
              </div>

              {/* Leyenda - Debajo del precio, centrada */}
              {banner.legend_text && (
                <div className="text-center" style={{ paddingTop: '20px', paddingBottom: '10px' }}>
                  <span 
                    style={{
                      ...getFontStyle(banner.legend_font, banner.legend_font_size || 16),
                      color: banner.legend_text_color || '#000000'
                    }}
                  >
                    {banner.legend_text}{' '}
                    <span 
                      style={{
                        color: banner.legend_price_color || '#000000',
                        fontWeight: 'bold'
                      }}
                    >
                      {formatPrice(gigantePrice)}
                    </span>
                    {' '}Adicionales
                  </span>
                </div>
              )}
            </div>
          ) : (
            // LAYOUT HORIZONTAL: Mantener el layout original de dos columnas (sin cambios)
            <div 
              className="flex flex-col"
              style={{
                width: '50%',
                maxWidth: '50%'
              }}
            >
              {/* Nombre del producto - Justo debajo de la imagen, centrado, mayúsculas, obligatorio */}
              <div 
                className="text-center"
                style={{
                  paddingTop: '2px',
                  paddingBottom: '10px'
                }}
              >
                <h2 
                  className="font-bold inline-block" 
                  style={{
                    ...getFontStyle(banner.product_name_font, banner.product_name_font_size || 24),
                    color: data?.screen?.colors?.title || '#000000',
                    textTransform: 'uppercase',
                    backgroundColor: banner.product_name_background_color || 'transparent',
                    padding: banner.product_name_background_color ? '4px 12px' : '0',
                    borderRadius: banner.product_name_background_color ? '4px' : '0'
                  }}
                >
                  {banner.product_name || 'Producto'}
                </h2>
              </div>

              {/* Dos columnas: Precio (izquierda) y Descripción (derecha) */}
              <div 
                className="flex gap-6"
                style={{
                  alignItems: 'center'
                }}
              >
                {/* Columna izquierda: Precio */}
                <div 
                  className="flex-1 flex flex-col items-end justify-center"
                >
                  <span 
                    style={{
                      ...getFontStyle(banner.price_font, banner.price_size || 24),
                      color: banner.price_color,
                      fontWeight: 'bold'
                    }}
                  >
                    {formatPrice(banner.producto_precio)}
                  </span>
                </div>

                {/* Columna derecha: Descripción en mayúsculas */}
                <div 
                  className="flex-1 flex flex-col items-start justify-center"
                >
                  {/* Palabra "Incluye" arriba de la descripción */}
                  <span 
                    style={{
                      ...getFontStyle(banner.legend_font, banner.legend_font_size || 16),
                      color: banner.legend_text_color || '#000000',
                      marginBottom: '8px'
                    }}
                  >
                    Incluye
                  </span>
                  {banner.description && (
                    <p 
                      style={{
                        ...getFontStyle(banner.description_font, banner.description_font_size || 18),
                        color: banner.description_color || '#000000',
                        textTransform: 'uppercase',
                        textAlign: 'left',
                        lineHeight: '1.2'
                      }}
                    >
                      {banner.description}
                    </p>
                  )}
                </div>
              </div>

              {/* Leyenda - Debajo de las dos columnas, centrada */}
              {banner.legend_text && (
                <div className="text-center" style={{ paddingTop: '10px', paddingBottom: '10px' }}>
                  <span 
                    style={{
                      ...getFontStyle(banner.legend_font, banner.legend_font_size || 16),
                      color: banner.legend_text_color || '#000000'
                    }}
                  >
                    {banner.legend_text}{' '}
                    <span 
                      style={{
                        color: banner.legend_price_color || '#000000',
                        fontWeight: 'bold'
                      }}
                    >
                      {formatPrice(gigantePrice)}
                    </span>
                    {' '}Adicionales
                  </span>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    );
  };

  // Componente interno para combo banners con slider (cuando banner_divisions = 1)
  const ComboBannersSlider: React.FC = () => {
    const [currentIndex, setCurrentIndex] = useState(0);
    
    // CRÍTICO: Todos los hooks deben ejecutarse ANTES de cualquier return condicional
    const comboBanners = useMemo(() => {
      if (!data?.combo_banners) return [];
      return [...data.combo_banners].sort((a, b) => (a.display_order || 0) - (b.display_order || 0));
    }, [data?.combo_banners]);

    // Auto-play slider con bucle infinito
    useEffect(() => {
      if (comboBanners.length <= 1) return;
      
      const currentBanner = comboBanners[currentIndex];
      const duration = (currentBanner?.duration || 5) * 1000; // Convertir a ms
      
      const timer = setTimeout(() => {
        setCurrentIndex((prev) => (prev + 1) % comboBanners.length);
      }, duration);

      return () => clearTimeout(timer);
    }, [currentIndex, comboBanners]);

    // AHORA SÍ: Después de todos los hooks, hacer validaciones y returns condicionales
    if (!data) return null;
    
    if (comboBanners.length === 0) {
      return (
        <div className="w-full h-full flex items-center justify-center text-white/70">
          No hay combo banners configurados
        </div>
      );
    }

    const currentBanner = comboBanners[currentIndex];
    if (!currentBanner) return null;

    return (
      <div className="w-full h-full relative">
        <ComboBannerContent banner={currentBanner} isVertical={isVertical} />

        {/* Indicadores de slider (si hay más de uno) */}
        {comboBanners.length > 1 && (
          <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 flex justify-center gap-2">
            {comboBanners.map((_, index) => (
              <button
                key={index}
                onClick={() => setCurrentIndex(index)}
                className={`w-2 h-2 rounded-full transition-all ${
                  index === currentIndex ? 'bg-white' : 'bg-white/50'
                }`}
              />
            ))}
          </div>
        )}
      </div>
    );
  };

  // Componente para mostrar 2 banners lado a lado (cuando banner_divisions = 2)
  const ComboBannersSplit: React.FC = () => {
    // CRÍTICO: Todos los hooks deben ejecutarse ANTES de cualquier return condicional
    const comboBanners = useMemo(() => {
      if (!data?.combo_banners) return [];
      // Filtrar solo los banners seleccionados para split (split_position = 1 o 2)
      return [...data.combo_banners]
        .filter(b => b.split_position === 1 || b.split_position === 2)
        .sort((a, b) => (a.split_position || 0) - (b.split_position || 0));
    }, [data?.combo_banners]);

    // AHORA SÍ: Después de todos los hooks, hacer validaciones y returns condicionales
    if (!data) return null;

    if (comboBanners.length === 0) {
      return (
        <div className="w-full h-full flex items-center justify-center text-white/70">
          No hay combo banners seleccionados para la pantalla dividida
        </div>
      );
    }

    // Si solo hay 1 banner, mostrar mensaje
    if (comboBanners.length < 2) {
      return (
        <div className="w-full h-full flex items-center justify-center text-white/70">
          Se requieren 2 combo banners seleccionados para la pantalla dividida. Actualmente hay {comboBanners.length}.
        </div>
      );
    }

    const leftBanner = comboBanners.find(b => b.split_position === 1);
    const rightBanner = comboBanners.find(b => b.split_position === 2);

    if (!leftBanner || !rightBanner) {
      return (
        <div className="w-full h-full flex items-center justify-center text-white/70">
          Error: No se encontraron los banners seleccionados para la pantalla dividida
        </div>
      );
    }

    return (
      <div className="w-full h-full flex">
        {/* Banner izquierdo */}
        <div className="w-1/2 h-full">
          <ComboBannerContent banner={leftBanner} isVertical={isVertical} />
        </div>

        {/* Banner derecho */}
        <div className="w-1/2 h-full">
          <ComboBannerContent banner={rightBanner} isVertical={isVertical} />
        </div>
      </div>
    );
  };

  // Componente para renderizar el slider publicitario
  const SliderComponent: React.FC<{
    images: Array<{
      id: number;
      image_path: string;
      image_url?: string;
      display_order: number;
      duration: number;
    }>;
    sliderType: 'fade' | 'slide' | 'zoom';
    backgroundColor: string;
  }> = ({ images, sliderType, backgroundColor }) => {
    const [currentIndex, setCurrentIndex] = useState(0);
    const [isTransitioning, setIsTransitioning] = useState(false);
    const [nextIndex, setNextIndex] = useState(0);

    // Ordenar imágenes por display_order
    const sortedImages = React.useMemo(() => {
      return [...images].sort((a, b) => a.display_order - b.display_order);
    }, [images]);

    // Efecto para cambiar automáticamente las imágenes
    useEffect(() => {
      if (sortedImages.length <= 1) return;

      const currentImage = sortedImages[currentIndex];
      if (!currentImage) return;
      
      const duration = (currentImage.duration || 5) * 1000; // Convertir a milisegundos

      const timer = setTimeout(() => {
        const next = (currentIndex + 1) % sortedImages.length;
        setNextIndex(next);
        setIsTransitioning(true);
        
        // Tiempo de transición según el tipo (más largos para mejor efecto)
        const transitionDuration = sliderType === 'fade' ? 1500 : sliderType === 'slide' ? 2000 : 2000;
        
        setTimeout(() => {
          setCurrentIndex(next);
          // Esperar a que termine la transición antes de resetear el flag
          setTimeout(() => {
            setIsTransitioning(false);
          }, 200);
        }, transitionDuration);
      }, duration);

      return () => clearTimeout(timer);
    }, [currentIndex, sortedImages, sliderType]);

    if (sortedImages.length === 0) {
      return (
        <div className="w-full h-full flex items-center justify-center text-white/70">
          No hay imágenes configuradas para el slider
        </div>
      );
    }

    // Estilos base para el contenedor del slider
    const containerStyle: React.CSSProperties = {
      width: '100%',
      height: '100%',
      position: 'relative',
      overflow: 'hidden',
      backgroundColor: backgroundColor,
      perspective: sliderType === 'slide' ? '1000px' : 'none',
      perspectiveOrigin: '50% 50%'
    };

    // Estilos para la imagen según el tipo de animación
    const getImageStyle = (index: number): React.CSSProperties => {
      const isActive = index === currentIndex;
      const isNext = index === nextIndex && isTransitioning;
      const baseStyle: React.CSSProperties = {
        position: 'absolute',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        objectFit: 'cover' as const
      };

      switch (sliderType) {
        case 'fade':
          // Fade mejorado con crossfade más pronunciado
          return {
            ...baseStyle,
            opacity: isActive ? 1 : isNext ? (isTransitioning ? 0.5 : 0) : 0,
            transition: isTransitioning 
              ? 'opacity 1.5s cubic-bezier(0.25, 0.46, 0.45, 0.94)' 
              : 'opacity 0.3s ease-in-out',
            zIndex: isActive ? 3 : isNext ? 2 : 1,
            filter: isActive ? 'none' : 'blur(2px)',
            transform: isActive ? 'scale(1)' : 'scale(1.05)'
          };
        
        case 'slide':
          // Slide mejorado con efecto de deslizamiento más dramático y lento
          const slideOffset = (index - currentIndex) * 100;
          const isAdjacent = Math.abs(index - currentIndex) === 1 || 
                            (index === sortedImages.length - 1 && currentIndex === 0) ||
                            (index === 0 && currentIndex === sortedImages.length - 1);
          
          // Calcular rotación y profundidad para efecto 3D
          const rotationY = (index - currentIndex) * 15; // Rotación en grados
          const translateZ = -Math.abs(index - currentIndex) * 50; // Profundidad
          const slideScaleValue = isActive ? 1 : isAdjacent ? 0.9 : 0.8;
          
          return {
            ...baseStyle,
            transform: `translateX(${slideOffset}%) translateZ(${translateZ}px) rotateY(${rotationY}deg) scale(${slideScaleValue})`,
            transition: isTransitioning && isAdjacent
              ? 'transform 2s cubic-bezier(0.16, 1, 0.3, 1), opacity 2s ease-in-out, filter 2s ease-in-out' 
              : 'none',
            zIndex: isActive ? 3 : isAdjacent ? 2 : 1,
            opacity: isActive ? 1 : isAdjacent ? 0.8 : 0,
            filter: isActive 
              ? 'none' 
              : isAdjacent 
                ? 'blur(3px) brightness(0.7)' 
                : 'blur(5px) brightness(0.5)',
            boxShadow: isActive 
              ? '0 10px 40px rgba(0, 0, 0, 0.3)' 
              : isAdjacent 
                ? '0 5px 20px rgba(0, 0, 0, 0.2)' 
                : 'none',
            transformStyle: 'preserve-3d' as const,
            backfaceVisibility: 'hidden' as const
          };
        
        case 'zoom':
          // Zoom mejorado con efecto Ken Burns (zoom continuo) y transición más dramática
          const isPrev = index < currentIndex || (index === sortedImages.length - 1 && currentIndex === 0);
          const zoomScaleValue = isActive 
            ? 1.1  // Zoom continuo sutil en la imagen activa
            : isPrev 
              ? 0.7  // Zoom out más pronunciado
              : 1.3; // Zoom in más pronunciado
          
          return {
            ...baseStyle,
            transform: `scale(${zoomScaleValue})`,
            opacity: isActive ? 1 : isTransitioning && isNext ? 0.4 : 0,
            transition: isTransitioning 
              ? 'transform 2s cubic-bezier(0.25, 0.46, 0.45, 0.94), opacity 2s ease-in-out' 
              : isActive
                ? 'transform 8s linear' // Zoom continuo lento en imagen activa
                : 'none',
            zIndex: isActive ? 3 : isTransitioning && isNext ? 2 : 1,
            filter: isActive ? 'none' : 'blur(2px)'
          };
        
        default:
          return baseStyle;
      }
    };

    return (
      <div style={containerStyle}>
        {sortedImages.map((image, index) => {
          const imageUrl = image.image_url || image.image_path;
          if (!imageUrl) return null;

          return (
            <img
              key={image.id}
              src={imageUrl}
              alt={`Slider ${index + 1}`}
              style={getImageStyle(index)}
              onError={(e) => {
                console.error('Error cargando imagen del slider:', imageUrl);
                (e.target as HTMLImageElement).style.display = 'none';
              }}
            />
          );
        })}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-black text-white text-lg">
        Cargando pantalla...
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-black text-white text-lg px-6 text-center">
        {error || 'No se pudo cargar la pantalla'}
      </div>
    );
  }

  const showProducts = data.screen.content_type !== 'banners_only' && data.screen.content_type !== 'advertising_only';
  const showBanners = data.screen.content_type !== 'menu_only' && data.screen.content_type !== 'advertising_only';
  const showBannersSection = data.screen.content_type === 'menu_and_banners';
  const showSlider = data.screen.content_type === 'advertising_only';
  const orientation = data.screen.orientation;
  const isHorizontal = orientation === 'horizontal';
  const isVertical = orientation === 'vertical';
  const bannerFirst = data.screen.banner_side !== 'right';

  // Log de depuración para condiciones de renderizado
  console.log('🔍 [DisplayScreen] Condiciones de renderizado:', {
    content_type: data.screen.content_type,
    showProducts,
    showBanners,
    showBannersSection,
    bannerFirst,
    hasSuperiorBanners: (data.banners?.superior?.length || 0) > 0,
    hasSuperiorSmartBanners: (data.smart_banners?.superior?.length || 0) > 0,
    hasLateralBanners: (data.banners?.laterales?.length || 0) > 0,
    hasLateralSmartBanners: (data.smart_banners?.laterales?.length || 0) > 0,
    superiorBannersCount: data.banners?.superior?.length || 0,
    superiorSmartBannersCount: data.smart_banners?.superior?.length || 0,
    lateralBannersCount: data.banners?.laterales?.length || 0,
    lateralSmartBannersCount: data.smart_banners?.laterales?.length || 0
  });

  // Calcular ancho de banners según products_count y banner_divisions
  const productsCount = data.screen.products_count || 10;
  const bannerDivisions = data.screen.banner_divisions || 2;
  // 33.333% para smart banners (6 productos con 3 o 4 divisiones)
  // 25% para banners normales (10 productos o 6 productos con 2 divisiones)
  const bannerWidth = (productsCount === 6 && (bannerDivisions === 3 || bannerDivisions === 4)) ? '33.333%' : '25%';
  
  // Calcular ancho de productos (el espacio que ocuparían si hubiera banners laterales)
  // Cuando no hay banners laterales (banner_divisions === 0), usamos este ancho para mantener el mismo tamaño
  const productsWidth = (productsCount === 6 && (bannerDivisions === 3 || bannerDivisions === 4)) ? '66.666%' : '75%';
  
  // Detectar si hay banners laterales
  const hasLateralBanners = (data.banners?.laterales?.length || 0) > 0 || (data.smart_banners?.laterales?.length || 0) > 0;

  // Tamaños fijos para el layout
  const BANNER_SUPERIOR_HEIGHT = 100; // px
  const PADDING_VERTICAL = 0; // px (py-4 = 16px)
  const GAP = 0; // px (gap-4 = 16px)
  
  // Calcular altura disponible para el contenido principal (sin header, sin extras)
  // El banner superior ahora está dentro del área de productos, así que no se resta aquí
  const availableHeight = `calc(100vh - ${PADDING_VERTICAL * 2}px - ${GAP}px)`;

  return (
    <div 
      className={bannerDivisions === 0 && isHorizontal && !hasLateralBanners ? 'overflow-visible' : 'overflow-hidden'}
      style={{
        ...themeStyles,
        width: '100vw',
        height: '100vh',
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0
      }}
    >
      {/* Banner superior FUERA de todos los contenedores cuando banner_divisions === 0 (diseño completo) */}
      {data.screen.content_type === 'menu_and_banners' && 
       bannerDivisions === 0 && 
       isHorizontal && 
       !hasLateralBanners && (
        <>
          {/* Smart Banners superiores */}
          {data.smart_banners?.superior && data.smart_banners.superior.length > 0 && (
            <div 
              className="fixed z-50"
              style={{ 
                top: '0px',
                left: '0px',
                right: '0px',
                maxHeight: `${BANNER_SUPERIOR_HEIGHT}px`,
                height: 'auto',
                width: '100vw',
                minWidth: '100vw',
                margin: 0,
                padding: 0,
                boxSizing: 'border-box',
                overflow: 'visible'
              }}
            >
                        {data.smart_banners.superior.slice(0, 1).map((banner, index) => (
                <div 
                  key={`smart-superior-${banner.id}-${banner.display_order}-${index}`} 
                  className="shadow-2xl"
                  style={{ 
                    margin: 0, 
                    padding: 0,
                    boxSizing: 'border-box',
                    maxHeight: `${BANNER_SUPERIOR_HEIGHT}px`,
                    height: 'auto',
                    width: '100vw',
                    minWidth: '100vw',
                    overflow: 'visible'
                  }}
                >
                  {renderSmartBanner(banner)}
                </div>
              ))}
            </div>
          )}
          
          {/* Banners normales superiores */}
          {data.banners.superior.length > 0 && (
            <div 
              className="fixed z-50"
              style={{ 
                top: '0px',
                left: '0px',
                right: '0px',
                maxHeight: `${BANNER_SUPERIOR_HEIGHT}px`,
                height: 'auto',
                width: '100vw',
                minWidth: '100vw',
                maxWidth: '100vw',
                margin: 0,
                padding: 0,
                boxSizing: 'border-box',
                overflow: 'visible',
                transform: 'translateX(0)'
              }}
            >
              {data.banners.superior.slice(0, 1).map((banner, index) => (
                <div 
                  key={`superior-${banner.id}-${banner.display_order}-${index}`} 
                  className="shadow-2xl bg-black/20"
                  style={{ 
                    margin: 0, 
                    padding: 0,
                    boxSizing: 'border-box',
                    maxHeight: `${BANNER_SUPERIOR_HEIGHT}px`,
                    height: 'auto',
                    width: '100vw',
                    minWidth: '100vw',
                    maxWidth: '100vw',
                    overflow: 'visible',
                    position: 'relative'
                  }}
                >
                  {banner.image_url ? (
                    <img 
                      src={banner.image_url} 
                      alt={banner.name} 
                      className="block"
                      style={{
                        objectPosition: 'top left',
                        objectFit: 'cover',
                        display: 'block',
                        maxHeight: `${BANNER_SUPERIOR_HEIGHT}px`,
                        height: 'auto',
                        width: '100vw',
                        minWidth: '100vw',
                        maxWidth: '100vw',
                        margin: 0,
                        padding: 0,
                        position: 'relative'
                      }}
                    />
                  ) : (
                    <div className="h-full flex items-center justify-center text-white/70">Banner sin imagen</div>
                  )}
                </div>
              ))}
            </div>
          )}
        </>
      )}

      <div 
        className="flex flex-col h-full"
        style={{
          padding: `${PADDING_VERTICAL}px`,
          gap: `${GAP}px`
        }}
      >
        {/* Contenido principal - ocupa el espacio restante */}
        <div 
          className={`flex ${bannerDivisions === 0 && isHorizontal && !hasLateralBanners ? 'overflow-visible' : 'overflow-hidden'}`}
          style={{
            // En vertical, usar 100vh directamente para que los porcentajes se calculen correctamente sobre el viewport completo
            height: isVertical ? '100vh' : availableHeight,
            flexDirection: isVertical ? 'column' : (isHorizontal ? 'row' : 'column'),
            gap: `${GAP}px`
          }}
        >
          {/* Si es advertising_only, mostrar slider */}
          {data.screen.content_type === 'advertising_only' ? (
            <div className="w-full h-full">
              <SliderComponent
                images={data.slider_images || []}
                sliderType={data.screen.slider_type || 'fade'}
                backgroundColor={data.screen.colors?.background || '#000000'}
              />
            </div>
          ) : data.screen.content_type === 'banners_only' ? (
            <div className="w-full h-full">
              {data.screen.banner_divisions === 2 ? (
                <ComboBannersSplit />
              ) : (
                <ComboBannersSlider />
              )}
            </div>
          ) : isVertical ? (
            /* LAYOUT VERTICAL */
            <>
              {/* Banners superiores arriba */}
              {data.screen.content_type === 'menu_and_banners' && (
                <>
                  {/* Smart Banners superiores */}
                  {data.smart_banners?.superior && data.smart_banners.superior.length > 0 && (
                    <div 
                      className="flex-shrink-0 w-full" 
                      style={{ 
                        height: '15%', 
                        maxHeight: '15%',
                        marginBottom: '0px',
                        overflow: 'hidden'
                      }}
                    >
                      <div className="flex flex-col gap-0 h-full" style={{ overflow: 'hidden' }}>
                        {data.smart_banners.superior.slice(0, 2).map((banner, index) => (
                          <div 
                            key={`smart-superior-${banner.id}-${banner.display_order}-${index}`} 
                            className="w-full shadow-2xl overflow-hidden" 
                            style={{ 
                              height: '50%',
                              flexShrink: 0,
                              overflow: 'hidden'
                            }}
                          >
                            {renderSmartBanner(banner, false, true)}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  
                  {/* Banners normales superiores */}
                  {data.banners?.superior && data.banners.superior.length > 0 && (
                    <div 
                      className="flex-shrink-0 w-full" 
                      style={{ 
                        height: '15%', 
                        maxHeight: '15%',
                        marginBottom: '0px',
                        overflow: 'hidden'
                      }}
                    >
                      <div className="flex flex-col gap-0 h-full" style={{ overflow: 'hidden' }}>
                        {data.banners.superior.slice(0, 2).map((banner, index) => (
                          <div 
                            key={`superior-${banner.id}-${banner.display_order}-${index}`} 
                            className="w-full shadow-2xl bg-black/20 overflow-hidden" 
                            style={{ 
                              height: '50%',
                              flexShrink: 0,
                              overflow: 'hidden'
                            }}
                          >
                            {banner.image_url ? (
                              <img 
                                src={banner.image_url} 
                                alt={banner.name} 
                                className="w-full h-full object-cover" 
                                style={{ 
                                  overflow: 'hidden',
                                  width: '100%',
                                  height: '100%'
                                }}
                              />
                            ) : (
                              <div className="h-full flex items-center justify-center text-white/70">Banner sin imagen</div>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </>
              )}
              
              {/* Productos en el medio */}
              {showProducts && (
                <div 
                  className="overflow-hidden min-h-0" 
                  style={{ 
                    // En vertical, usar 65% directamente; en horizontal, usar flex-1
                    height: isVertical ? '65%' : undefined,
                    flex: isVertical ? 'none' : 1,
                    padding: '20px' 
                  }}
                >
                  {renderProducts()}
                </div>
              )}
              
              {/* Banners inferiores abajo */}
              {data.screen.content_type === 'menu_and_banners' && (
                <>
                  {/* Smart Banners inferiores */}
                  {data.smart_banners?.inferior && data.smart_banners.inferior.length > 0 && (
                    <div 
                      className="flex-shrink-0 w-full" 
                      style={{ 
                        height: '20%', 
                        maxHeight: '20%',
                        marginTop: '0px',
                        overflow: 'hidden',
                        display: 'grid',
                        gridTemplateColumns: '1fr 1fr',
                        gap: isVertical ? '0px' : '8px',  // Sin gap en vertical
                        padding: '0 4px'
                      }}
                    >
                      {isVertical ? (() => {
                        // Ordenar banners por display_order solo en vertical
                        const sortedBanners = [...data.smart_banners.inferior]
                          .filter(banner => banner.display_order === 1 || banner.display_order === 2)
                          .sort((a, b) => (a.display_order || 0) - (b.display_order || 0))
                          .slice(0, 2);
                        
                        // Crear un array de 2 posiciones (izquierda y derecha)
                        const bannersByPosition: Array<typeof sortedBanners[0] | null> = [null, null];
                        
                        sortedBanners.forEach(banner => {
                          const order = banner.display_order || 0;
                          if (order === 1) {
                            bannersByPosition[0] = banner; // Izquierda
                          } else if (order === 2) {
                            bannersByPosition[1] = banner; // Derecha
                          }
                        });
                        
                        return bannersByPosition.map((banner, index) => {
                          if (!banner) return null;
                          
                          return (
                            <div 
                              key={`smart-inferior-${banner.id}-${banner.display_order}-${index}`} 
                              className="shadow-2xl overflow-hidden h-full"
                              style={{
                                overflow: 'hidden',
                                width: '100%',
                                height: '100%'
                              }}
                            >
                              {renderSmartBanner(banner, false, true)}
                            </div>
                          );
                        });
                      })() : (
                        // En horizontal, mantener el comportamiento original
                        data.smart_banners.inferior.slice(0, 2).map((banner, index) => (
                          <div 
                            key={`smart-inferior-${banner.id}-${banner.display_order}-${index}`} 
                            className="shadow-2xl overflow-hidden h-full"
                            style={{
                              overflow: 'hidden',
                              width: '100%',
                              height: '100%'
                            }}
                          >
                            {renderSmartBanner(banner, false, false)}
                          </div>
                        ))
                      )}
                    </div>
                  )}
                  
                  {/* Banners normales inferiores */}
                  {data.banners?.inferior && data.banners.inferior.length > 0 && (
                    <div 
                      className="flex-shrink-0 w-full" 
                      style={{ 
                        height: '20%', 
                        maxHeight: '20%',
                        marginTop: '0px',
                        overflow: 'hidden'
                      }}
                    >
                      {data.banners.inferior.slice(0, 1).map((banner, index) => (
                        <div 
                          key={`inferior-${banner.id}-${banner.display_order}-${index}`} 
                          className="shadow-2xl bg-black/20 overflow-hidden w-full h-full"
                          style={{
                            overflow: 'hidden'
                          }}
                        >
                          {banner.image_url ? (
                            <img 
                              src={banner.image_url} 
                              alt={banner.name} 
                              className="w-full h-full object-contain" 
                              style={{ overflow: 'hidden' }}
                            />
                          ) : (
                            <div className="h-full flex items-center justify-center text-white/70">Banner sin imagen</div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </>
              )}
            </>
          ) : (
            /* LAYOUT HORIZONTAL (código existente) */
            <>
              {/* Solo renderizar sección de banners laterales si hay banners laterales */}
              {(data.screen.content_type === 'menu_and_banners' && bannerFirst && 
                ((data.banners?.laterales?.length || 0) > 0 || (data.smart_banners?.laterales?.length || 0) > 0)) && (
                <div 
                  className="h-full overflow-hidden flex-shrink-0"
                  style={{
                    width: isHorizontal && showProducts ? bannerWidth : '100%'
                  }}
                >
                  {renderBannerSection(isHorizontal ? 'side' : 'full')}
                </div>
              )}

              {showProducts && (
            <div 
              className={`h-full flex flex-col ${bannerDivisions === 0 && isHorizontal && !hasLateralBanners ? 'overflow-visible relative' : 'overflow-hidden'}`}
              style={{
                flex: showBanners ? '1' : '1',
                minWidth: 0,
                minHeight: 0,
                gap: '0px',
                // Si no hay banners laterales, limitar el ancho y alinear a la izquierda
                ...(bannerDivisions === 0 && isHorizontal && !hasLateralBanners ? {
                  maxWidth: productsWidth,
                  margin: 0,
                  marginLeft: 0,
                  width: '100%',
                  // Agregar padding-top para el banner superior cuando está fixed
                  paddingTop: ((data.banners?.superior?.length ?? 0) > 0 || (data.smart_banners?.superior?.length ?? 0) > 0) 
                    ? `${BANNER_SUPERIOR_HEIGHT + 10}px` 
                    : '0px'
                } : {})
              }}
            >
              {/* Banners superiores - dentro del área de productos SOLO cuando NO es diseño completo */}
              {data.screen.content_type === 'menu_and_banners' && 
               !(bannerDivisions === 0 && isHorizontal && !hasLateralBanners) && (
                <>
                  {/* Smart Banners superiores */}
                  {data.smart_banners?.superior && data.smart_banners.superior.length > 0 && (
                    <div 
                      className="flex-shrink-0 overflow-hidden w-full"
                      style={{ 
                        maxHeight: `${BANNER_SUPERIOR_HEIGHT}px`,
                        height: 'auto',
                        marginTop: '0px', 
                        paddingTop: '0px', 
                        marginBottom: '10px', 
                        paddingBottom: '0px',
                        boxSizing: 'border-box'
                      }}
                    >
                      {data.smart_banners.superior.slice(0, 1).map((banner, index) => (
                        <div 
                          key={`smart-superior-${banner.id}-${banner.display_order}-${index}`} 
                          className="shadow-2xl overflow-hidden w-full"
                          style={{ 
                            margin: '0px', 
                            padding: '0px',
                            boxSizing: 'border-box',
                            maxHeight: `${BANNER_SUPERIOR_HEIGHT}px`,
                            height: 'auto',
                            width: '100%',
                            overflow: 'hidden'
                          }}
                        >
                          {renderSmartBanner(banner)}
                        </div>
                      ))}
                    </div>
                  )}
                  
                  {/* Banners normales superiores */}
                  {data.banners.superior.length > 0 && (
                    <div 
                      className="flex-shrink-0 overflow-hidden w-full"
                      style={{ 
                        maxHeight: `${BANNER_SUPERIOR_HEIGHT}px`,
                        height: 'auto',
                        marginTop: '0px', 
                        paddingTop: '0px', 
                        marginBottom: '10px', 
                        paddingBottom: '0px',
                        boxSizing: 'border-box'
                      }}
                    >
                      {data.banners.superior.slice(0, 1).map((banner, index) => (
                        <div 
                          key={`superior-${banner.id}-${banner.display_order}-${index}`} 
                          className={`shadow-2xl bg-black/20 ${bannerDivisions === 0 && isHorizontal && !hasLateralBanners ? '' : 'overflow-hidden w-full'}`}
                          style={{ 
                            margin: '0px', 
                            padding: '0px',
                            boxSizing: 'border-box',
                            maxHeight: `${BANNER_SUPERIOR_HEIGHT}px`,
                            height: 'auto',
                            // Si no hay banners laterales, usar 100vw
                            ...(bannerDivisions === 0 && isHorizontal && !hasLateralBanners ? {
                              width: '100vw',
                              minWidth: '100vw',
                              overflow: 'visible'
                            } : {
                              width: '100%',
                              overflow: 'hidden'
                            })
                          }}
                        >
                          {banner.image_url ? (
                            <img 
                              src={banner.image_url} 
                              alt={banner.name} 
                              className={bannerDivisions === 0 && isHorizontal && !hasLateralBanners ? '' : 'h-auto object-contain'}
                              style={{
                                objectPosition: (bannerDivisions === 0 && isHorizontal && !hasLateralBanners) ? 'top left' : 'top',
                                objectFit: (bannerDivisions === 0 && isHorizontal && !hasLateralBanners) ? 'cover' : 'contain',
                                display: 'block',
                                maxHeight: `${BANNER_SUPERIOR_HEIGHT}px`,
                                height: 'auto',
                                width: (bannerDivisions === 0 && isHorizontal && !hasLateralBanners) ? '100vw' : '100%',
                                minWidth: (bannerDivisions === 0 && isHorizontal && !hasLateralBanners) ? '100vw' : undefined
                              }}
                            />
                          ) : (
                            <div className="h-full flex items-center justify-center text-white/70">Banner sin imagen</div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </>
              )}
              
              {/* Productos - ocupan el espacio restante */}
              <div 
                className="flex-1 overflow-hidden min-h-0" 
                style={{ 
                  paddingTop: (bannerDivisions === 0 && isHorizontal && !hasLateralBanners && 
                              ((data.banners?.superior?.length ?? 0) > 0 || (data.smart_banners?.superior?.length ?? 0) > 0)) 
                              ? '0px' : '0px', 
                  paddingRight: '20px', 
                  paddingBottom: '20px', 
                  paddingLeft: '20px' 
                }}
              >
                {renderProducts()}
              </div>
            </div>
          )}

          {/* Solo renderizar sección de banners laterales si hay banners laterales */}
          {data.screen.content_type === 'menu_and_banners' && !bannerFirst && 
           ((data.banners?.laterales?.length || 0) > 0 || (data.smart_banners?.laterales?.length || 0) > 0) && (
            <div 
              className="h-full overflow-hidden flex-shrink-0"
              style={{
                width: isHorizontal && showProducts ? bannerWidth : '100%'
              }}
            >
              {renderBannerSection(isHorizontal ? 'side' : 'full')}
            </div>
          )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

